/**
 * Murphy Door Configurator - Cascade Auto-Selection Coordinator
 *
 * PURPOSE: Event-driven cascade coordinator that subscribes to the previously dead
 * selection:auto_apply_request event and orchestrates sequential auto-selection
 * across configurator categories with manual selection protection.
 *
 * PHASE: 2 (Event-Driven CCP - Strict Enforcement)
 * MODULE TYPE: Business Logic (bl-)
 * GOLDEN STANDARD REFERENCE: md-config-bl-auto-navigation-coordinator.js (similar structure)
 *
 * CCP COMPLIANCE:
 * - All events use CONSTANTS.EVENTS.* centralized constants (Pattern #19)
 * - Event-driven coordination ONLY (no direct module calls)
 * - PPD-First data access via conductor delegation
 * - Conductor delegation for state changes (handleStateChange)
 * - 5-stage event lifecycle for cascade operations (START → VALIDATION → PROGRESS → SUCCESS → FAILED)
 * - Bootstrap barrier pattern for race protection
 * - Object.freeze() NOT called on event payloads - CoreEventBus.publish() freezes centrally (Pattern #18 DRY)
 * - Performance tracking with configurable thresholds
 * - Comprehensive cleanup for all event subscriptions
 * - Sequential queue with lock flag prevents concurrent cascades
 * - Manual selection protection (user choices are sacred)
 * - MAX_CASCADE_DEPTH safety valve (10 levels)
 *
 * CCP EXCEPTIONS RESOLVED (Phase 2 Modernization):
 * - conductor.clearCategoryState() delegation method now available (replaces direct stateManager access)
 * - conductor.evaluateAutoApplyRulesOnClear() delegation method now available (replaces direct forbiddenValidator access)
 *
 * RESPONSIBILITIES:
 * 1. Subscribe to selection:auto_apply_request (THE MISSING SUBSCRIBER!)
 * 2. Check if category has manual selection (SKIP if yes - user intent sacred)
 * 3. Queue auto-selection for sequential processing
 * 4. Maintain lock flag (cascadeInProgress) to prevent concurrent cascades
 * 5. Execute auto-apply via conductor delegation: handleStateChange('APPLY_SELECTION', ...)
 * 6. Each apply triggers selection:variant_applied -> which may trigger MORE auto_apply_requests
 * 7. Detect cascade termination via 50ms debounce window
 * 8. Publish cascade lifecycle events at each stage
 * 9. Enforce MAX_CASCADE_DEPTH = 10 safety valve
 *
 * EVENT FLOW:
 * USER SELECTS VARIANT (any category)
 *   -> conductor.handleStateChange('APPLY_SELECTION', ...)
 *   -> publishes selection:variant_applied
 *   -> materialPreferenceCoordinator.computeAndPublishFilterState()
 *   -> publishes selection:auto_apply_request (previously dead!)
 *   -> cascadeAutoSelectionCoordinator (THIS MODULE - THE MISSING SUBSCRIBER!)
 *   -> publishes business:cascade_auto_selection_started
 *   -> CHECK: manualSelections.has(categoryId)? SKIP : QUEUE
 *   -> PROCESS: Sequential queue with await
 *   -> Each apply may trigger MORE auto_apply_requests
 *   -> CASCADE continues until queue drains
 *   -> TERMINATE: After 50ms debounce with no new requests
 *   -> publishes business:cascade_auto_selection_completed
 *
 * @version 1.0.0
 * @author Murphy Door Configurator Team
 */
(function (window) {
  'use strict';

  // Namespace initialization
  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};
  const CoreEventBus = window.MurphyDoorConfigurator.CoreEventBus;
  const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;

  // Dev-only logging (production guard)
  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  // ============================================================================
  // PATTERN 1: GLOBAL CONSTANTS USAGE (10 points)
  // ============================================================================
  /**
   * Using global CONSTANTS from md-config-core-constants.js
   * This eliminates magic strings and ensures consistency across modules.
   * All event names are defined once in the global constants file.
   */

  // ============================================================================
  // PATTERN 13: PERFORMANCE THRESHOLDS (10 points)
  // ============================================================================
  const PERFORMANCE_THRESHOLDS = Object.freeze({
    SINGLE_STEP: 100, // 100ms per individual auto-apply step
    TOTAL_CASCADE: 500, // 500ms for entire cascade sequence
    QUEUE_PROCESSING: 200, // 200ms for queue drain cycle
  });

  // ============================================================================
  // CASCADE CONFIGURATION
  // ============================================================================
  const CASCADE_CONFIG = Object.freeze({
    MAX_DEPTH: 10, // Safety valve: maximum cascade depth
    TERMINATION_DEBOUNCE_MS: 50, // Debounce window for cascade termination detection
  });

  // ============================================================================
  // PATTERN 16: INITIALIZATION GUARD (10 points)
  // ============================================================================
  let isInitialized = false;

  // ============================================================================
  // PATTERN 6: BOOTSTRAP RUNTIME PROTECTION (10 points)
  // ============================================================================
  let bootstrapComplete = false;
  let pendingCalls = [];

  // ============================================================================
  // PATTERN 15: CLEANUP ARRAYS (10 points)
  // ============================================================================
  let cleanupHandlers = [];

  // ============================================================================
  // LOCAL COORDINATION STATE (CCP-compliant flags)
  // ============================================================================

  /**
   * Tracks categories where the user has manually applied a selection.
   * Manual selections are SACRED - auto-apply will NEVER override these.
   * Map<categoryId, { variantId, timestamp }>
   */
  const manualSelections = new Map();

  /**
   * Tracks categories where this module has auto-applied a selection.
   * Used to avoid duplicate auto-applies to the same category within a cascade.
   * Map<categoryId, { variantId, timestamp }>
   */
  const autoAppliedCategories = new Map();

  /**
   * Tracks category IDs currently marked as "stale" in the UI.
   * A stale selection is an auto-applied option that may be orphaned after
   * the material preference that drove the auto-apply was cleared or switched.
   * Set<categoryId>
   */
  const staleCategoryIds = new Set();

  /**
   * Sequential cascade queue. Requests are processed one at a time.
   * Each entry: { variantId, categoryId, reason, source, timestamp }
   */
  let cascadeQueue = [];

  /**
   * Lock flag: true while a cascade step is being processed.
   * Prevents concurrent cascade processing.
   */
  let cascadeInProgress = false;

  /**
   * Current cascade depth counter. Reset when cascade terminates.
   * Safety valve at MAX_CASCADE_DEPTH prevents infinite loops.
   */
  let cascadeDepth = 0;

  /**
   * Cascade session ID. Incremented on each new cascade start.
   * Used to correlate lifecycle events within a single cascade.
   */
  let cascadeSessionId = 0;

  /**
   * Termination debounce timer. After queue drains, wait this long
   * before declaring the cascade complete (new requests may arrive).
   */
  let terminationTimer = null;

  /**
   * Total cascade start time for performance tracking.
   */
  let cascadeStartTime = 0;

  // ============================================================================
  // AUTO_APPLY_OPTION STATE TRACKING
  // ============================================================================

  /**
   * Tracks active auto-apply rules that have been successfully applied.
   * Used to determine which rules need to be un-applied when conditions change.
   * Map<ruleId, { conditionVariantId, targetVariantId, targetCategoryId, targetProductId, timestamp }>
   */
  const activeAutoApplyRules = new Map();

  /**
   * Tracks auto-apply coupling chains to detect circular dependencies.
   * Key = targetVariantId, Value = { sourceVariantId, ruleId, depth }
   * Used by detectCircularAutoApply() to prevent A -> B -> A infinite loops.
   */
  const autoApplyChains = new Map();

  /**
   * Maximum depth for auto-apply chain traversal.
   * Prevents runaway recursion in complex multi-hop rule chains.
   */
  const MAX_AUTO_APPLY_CHAIN_DEPTH = 10;

  // ============================================================================
  // MODULE REFERENCES
  // ============================================================================
  let eventBus = null;

  // ============================================================================
  // BOOTSTRAP SUBSCRIPTION (MODULE LOAD TIME - RACE PROTECTION)
  // ============================================================================
  if (CoreEventBus) {
    // PATTERN 15: Track bootstrap subscription for cleanup
    const unsubBootstrap = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.SYSTEM_STATE_FROM_PPD_READY,
      function () {
        isDev() &&
          console.log(
            '[CascadeAutoSelectionCoordinator] Bootstrap complete event received'
          );
        bootstrapComplete = true;
        eventBus = CoreEventBus;

        // Process all queued calls
        if (pendingCalls.length > 0) {
          isDev() &&
            console.log(
              '[CascadeAutoSelectionCoordinator] Processing ' +
                pendingCalls.length +
                ' queued calls'
            );
          pendingCalls.forEach(function (call) {
            try {
              call.fn.apply(null, call.args);
            } catch (error) {
              isDev() &&
                console.error(
                  '[CascadeAutoSelectionCoordinator] Error processing queued call:',
                  error
                );
            }
          });
          pendingCalls = [];
        }
      }
    );
    cleanupHandlers.push(unsubBootstrap);

    // C2 CURATED DEFAULTS: subscribe at MODULE LOAD (before any bootstrap event
    // fires) so the load-time BUSINESS_CASCADE_APPLY_CURATED_DEFAULTS publish
    // (fired on SYSTEM_STATE_FROM_PPD_READY by the curated coordinator) is never
    // missed. initialize() runs later on SYSTEM_MAPS_INITIALIZED, so its
    // subscriptions would be too late for this specific load-time event. The
    // conductor is resolved lazily inside the handler (it exists by the time the
    // event fires, post-bootstrap).
    const unsubCuratedDefaults = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.BUSINESS_CASCADE_APPLY_CURATED_DEFAULTS,
      function (eventData) {
        const conductor =
          window.MurphyDoorConfigurator.conductor ||
          window.MurphyDoorConfigurator.main;
        if (!conductor) {
          isDev() &&
            console.error(
              '[CascadeAutoSelectionCoordinator] Conductor unavailable for curated defaults'
            );
          return;
        }
        handleSimplifiedDefaultsRequest(eventData, conductor);
      }
    );
    cleanupHandlers.push(unsubCuratedDefaults);

    isDev() &&
      console.log(
        '[CascadeAutoSelectionCoordinator] Subscribed to bootstrap event at module load time'
      );
  }

  // ============================================================================
  // PATTERN 2: publishEvent() HELPER (10 points)
  // ============================================================================
  /**
   * Centralized event publishing with error handling.
   * All events use CONSTANTS.EVENTS.* centralized constants.
   *
   * PATTERN 11: Async Event Publishing
   * PATTERN 18: Object.freeze() NOT needed - CoreEventBus.publish() freezes centrally (DRY)
   *
   * @param {string} eventType - Event type constant from CONSTANTS.EVENTS
   * @param {Object} detail - Event payload data
   * @param {string} [context='operation'] - Operation context for debugging
   * @returns {boolean} Success status
   */
  function publishEvent(eventType, detail, context) {
    context = context || 'operation';
    var bus = eventBus || window.MurphyDoorConfigurator.CoreEventBus;

    if (!bus) {
      isDev() &&
        console.warn(
          '[CascadeAutoSelectionCoordinator] EventBus not available, cannot publish: ' +
            eventType
        );
      return false;
    }

    try {
      // PATTERN 18: Object.freeze() NOT needed - CoreEventBus.publish() freezes centrally (DRY)
      var enrichedDetail = {
        ...detail,
        timestamp: detail.timestamp || Date.now(),
        source: detail.source || 'CascadeAutoSelectionCoordinator',
      };

      var success = bus.publish(eventType, enrichedDetail);

      if (!success) {
        isDev() &&
          console.warn(
            '[CascadeAutoSelectionCoordinator] Event publication returned false: ' +
              eventType,
            { context: context }
          );
      }

      return success;
    } catch (error) {
      isDev() &&
        console.error(
          '[CascadeAutoSelectionCoordinator] Failed to publish event ' +
            eventType +
            ':',
          error
        );

      // Attempt to publish failure event (avoid recursion on FAILED events)
      // Uses CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_FAILED for centralized constant usage
      if (
        eventType !== CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_FAILED &&
        bus
      ) {
        try {
          // NOTE: No Object.freeze() - CoreEventBus.publish() freezes centrally (Pattern #18 DRY)
          bus.publish(CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_FAILED, {
            error: error.message,
            context: context,
            originalEvent: eventType,
            timestamp: Date.now(),
            source: 'CascadeAutoSelectionCoordinator',
          });
        } catch (nestedError) {
          isDev() &&
            console.error(
              '[CascadeAutoSelectionCoordinator] Failed to publish failure event:',
              nestedError
            );
        }
      }

      return false;
    }
  }

  // ============================================================================
  // PATTERN 12: PERFORMANCE TRACKING (10 points)
  // ============================================================================
  /**
   * Track operation performance and publish slow operation events.
   *
   * @param {string} operationType - Type of operation being tracked
   * @param {number} duration - Duration in milliseconds
   * @param {Object} [metadata={}] - Additional metadata for the performance event
   */
  function trackPerformance(operationType, duration, metadata) {
    metadata = metadata || {};
    var threshold = PERFORMANCE_THRESHOLDS[operationType];

    if (threshold && duration > threshold) {
      publishEvent(
        CONSTANTS.EVENTS.PERFORMANCE_CASCADE_AUTO_SELECTION_SLOW,
        {
          operation: operationType,
          duration: parseFloat(duration.toFixed(2)),
          threshold: threshold,
          severity: duration > threshold * 2 ? 'critical' : 'warning',
          ...metadata,
        },
        'performance_tracking'
      );

      isDev() &&
        console.warn(
          '[CascadeAutoSelectionCoordinator] PERFORMANCE: ' +
            operationType +
            ' took ' +
            duration.toFixed(2) +
            'ms ' +
            '(threshold: ' +
            threshold +
            'ms)'
        );
    }
  }

  // ============================================================================
  // CORE BUSINESS LOGIC: handleAutoApplyRequest
  // ============================================================================
  /**
   * Handles an incoming selection:auto_apply_request event.
   * This is THE MISSING SUBSCRIBER that makes the cascade system work.
   *
   * PATTERN 3: 5-Stage Event Lifecycle
   * PATTERN 14: Try/Catch + Failed Events
   * PATTERN 17: XSS Prevention (validates event data types)
   *
   * @param {Object} eventData - Event payload from selection:auto_apply_request
   * @param {Object} conductor - The central conductor for delegation
   */
  function handleAutoApplyRequest(eventData, conductor) {
    var METHOD_SIGNATURE =
      'CascadeAutoSelectionCoordinator.handleAutoApplyRequest';

    try {
      var detail = eventData.detail || eventData;

      // PATTERN 17: XSS Prevention - validate data types
      var variantId = detail.variantId;
      var categoryId =
        typeof detail.categoryId === 'string'
          ? detail.categoryId
          : String(detail.categoryId || '');
      var reason =
        typeof detail.reason === 'string' ? detail.reason : 'unknown';

      if (!variantId || !categoryId) {
        isDev() &&
          console.warn(
            '[' +
              METHOD_SIGNATURE +
              '] Invalid auto_apply_request: missing variantId or categoryId',
            { variantId: variantId, categoryId: categoryId }
          );
        return;
      }

      isDev() &&
        console.log('[' + METHOD_SIGNATURE + '] Received auto_apply_request', {
          variantId: variantId,
          categoryId: categoryId,
          reason: reason,
        });

      // STAGE 2: VALIDATION - Check guards before queuing
      // GUARD 1: Skip if category has manual selection (user intent is sacred!)
      if (manualSelections.has(categoryId)) {
        var manualInfo = manualSelections.get(categoryId);
        isDev() &&
          console.log(
            '[' +
              METHOD_SIGNATURE +
              '] SKIPPED - category "' +
              categoryId +
              '" has manual selection (variant: ' +
              manualInfo.variantId +
              ')'
          );

        publishEvent(
          CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_SKIPPED,
          {
            categoryId: categoryId,
            variantId: variantId,
            reason: 'manual_selection_exists',
            manualVariantId: manualInfo.variantId,
            cascadeSessionId: cascadeSessionId,
          },
          METHOD_SIGNATURE
        );

        publishEvent(
          CONSTANTS.EVENTS.SELECTION_AUTO_APPLY_SKIPPED,
          {
            categoryId: categoryId,
            variantId: variantId,
            reason: 'manual_selection_exists',
          },
          METHOD_SIGNATURE
        );

        return;
      }

      // GUARD 2: Skip if already auto-applied same variant to this category in current session
      var existingAutoApply = autoAppliedCategories.get(categoryId);
      if (existingAutoApply && existingAutoApply.variantId === variantId) {
        isDev() &&
          console.log(
            '[' +
              METHOD_SIGNATURE +
              '] SKIPPED - already auto-applied variant ' +
              variantId +
              ' to category "' +
              categoryId +
              '"'
          );

        publishEvent(
          CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_SKIPPED,
          {
            categoryId: categoryId,
            variantId: variantId,
            reason: 'already_auto_applied',
            cascadeSessionId: cascadeSessionId,
          },
          METHOD_SIGNATURE
        );

        return;
      }

      // Clear termination timer - new request means cascade is still active
      if (terminationTimer) {
        clearTimeout(terminationTimer);
        terminationTimer = null;
      }

      // Start new cascade session if not currently cascading
      if (
        cascadeDepth === 0 &&
        !cascadeInProgress &&
        cascadeQueue.length === 0
      ) {
        cascadeSessionId++;
        cascadeStartTime = window.performance
          ? window.performance.now()
          : Date.now();

        // STAGE 1: CASCADE STARTED
        publishEvent(
          CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_STARTED,
          {
            sessionId: cascadeSessionId,
            triggerVariantId: variantId,
            triggerCategoryId: categoryId,
            reason: reason,
          },
          METHOD_SIGNATURE
        );

        isDev() &&
          console.log(
            '[' +
              METHOD_SIGNATURE +
              '] CASCADE STARTED (session: ' +
              cascadeSessionId +
              ')'
          );
      }

      // Enqueue the request
      cascadeQueue.push({
        variantId: variantId,
        categoryId: categoryId,
        reason: reason,
        source: detail.source || 'unknown',
        timestamp: Date.now(),
      });

      // Process queue (will be a no-op if already processing)
      processCascadeQueue(conductor);
    } catch (error) {
      isDev() && console.error('[' + METHOD_SIGNATURE + '] Failed:', error);

      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_FAILED,
        {
          error: error.message,
          stack: error.stack,
          cascadeSessionId: cascadeSessionId,
          source: METHOD_SIGNATURE,
        },
        METHOD_SIGNATURE
      );
    }
  }

  // ============================================================================
  // CORE BUSINESS LOGIC: processCascadeQueue
  // ============================================================================
  /**
   * Processes the cascade queue sequentially with lock protection.
   *
   * PATTERN 7: Perfect Dictionary Only (PPD-First via conductor delegation)
   * PATTERN 8: No Raw Data Access (all data through conductor)
   * PATTERN 9: Data Request via conductor delegation
   *
   * @param {Object} conductor - The central conductor for delegation
   */
  function processCascadeQueue(conductor) {
    var METHOD_SIGNATURE =
      'CascadeAutoSelectionCoordinator.processCascadeQueue';

    // Lock check - already processing
    if (cascadeInProgress) {
      return;
    }

    // Queue empty - start termination debounce
    if (cascadeQueue.length === 0) {
      scheduleTermination();
      return;
    }

    cascadeInProgress = true;
    var request = cascadeQueue.shift();
    var stepStartTime = window.performance
      ? window.performance.now()
      : Date.now();

    try {
      // GUARD: Depth limit safety valve
      if (cascadeDepth >= CASCADE_CONFIG.MAX_DEPTH) {
        isDev() &&
          console.error(
            '[' +
              METHOD_SIGNATURE +
              '] MAX_CASCADE_DEPTH (' +
              CASCADE_CONFIG.MAX_DEPTH +
              ') exceeded! Aborting cascade.'
          );

        publishEvent(
          CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_FAILED,
          {
            error: 'max_depth_exceeded',
            maxDepth: CASCADE_CONFIG.MAX_DEPTH,
            currentDepth: cascadeDepth,
            cascadeSessionId: cascadeSessionId,
            droppedRequest: {
              categoryId: request.categoryId,
              variantId: request.variantId,
            },
            queueRemaining: cascadeQueue.length,
          },
          METHOD_SIGNATURE
        );

        // Flush remaining queue
        cascadeQueue = [];
        cascadeInProgress = false;
        cascadeDepth = 0;
        return;
      }

      // GUARD: Re-check manual selection (may have changed since enqueue)
      if (manualSelections.has(request.categoryId)) {
        isDev() &&
          console.log(
            '[' +
              METHOD_SIGNATURE +
              '] SKIPPED step - manual selection appeared for category "' +
              request.categoryId +
              '"'
          );

        publishEvent(
          CONSTANTS.EVENTS.SELECTION_AUTO_APPLY_SKIPPED,
          {
            categoryId: request.categoryId,
            variantId: request.variantId,
            reason: 'manual_selection_appeared',
          },
          METHOD_SIGNATURE
        );

        cascadeInProgress = false;
        processCascadeQueue(conductor);
        return;
      }

      // GUARD: Re-check duplicate auto-apply
      var existingAutoApply = autoAppliedCategories.get(request.categoryId);
      if (
        existingAutoApply &&
        existingAutoApply.variantId === request.variantId
      ) {
        isDev() &&
          console.log(
            '[' +
              METHOD_SIGNATURE +
              '] SKIPPED step - already auto-applied variant ' +
              request.variantId +
              ' to "' +
              request.categoryId +
              '"'
          );

        cascadeInProgress = false;
        processCascadeQueue(conductor);
        return;
      }

      cascadeDepth++;

      // PATTERN 7 & 9: Resolve productId from variantId via conductor delegation (PPD-first)
      var productId = resolveProductId(request.variantId, conductor);

      if (!productId) {
        isDev() &&
          console.warn(
            '[' +
              METHOD_SIGNATURE +
              '] Could not resolve productId for variant ' +
              request.variantId +
              ' - skipping'
          );

        publishEvent(
          CONSTANTS.EVENTS.SELECTION_AUTO_APPLY_SKIPPED,
          {
            categoryId: request.categoryId,
            variantId: request.variantId,
            reason: 'product_not_found',
          },
          METHOD_SIGNATURE
        );

        cascadeInProgress = false;
        processCascadeQueue(conductor);
        return;
      }

      isDev() &&
        console.log(
          '[' +
            METHOD_SIGNATURE +
            '] EXECUTING auto-apply step ' +
            cascadeDepth,
          {
            categoryId: request.categoryId,
            productId: productId,
            variantId: request.variantId,
            sessionId: cascadeSessionId,
          }
        );

      // EXECUTE: Apply via conductor delegation (CCP compliant)
      // This triggers the full APPLY_SELECTION flow which publishes selection:variant_applied
      // which triggers materialPreferenceCoordinator which may publish MORE auto_apply_requests
      conductor.handleStateChange('APPLY_SELECTION', {
        categoryId: request.categoryId,
        productId: productId,
        variantId: request.variantId,
        auto_applied: true,
        isOneStep: true,
      });

      // Track this auto-apply (reason included for stale detection filtering)
      autoAppliedCategories.set(request.categoryId, {
        variantId: request.variantId,
        timestamp: Date.now(),
        reason: request.reason || 'cascade',
      });

      // STAGE 3: STEP EVENT
      var stepDuration =
        (window.performance ? window.performance.now() : Date.now()) -
        stepStartTime;

      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_STEP,
        {
          categoryId: request.categoryId,
          productId: productId,
          variantId: request.variantId,
          stepNumber: cascadeDepth,
          cascadeSessionId: cascadeSessionId,
          duration: parseFloat(stepDuration.toFixed(2)),
          reason: request.reason,
          queueRemaining: cascadeQueue.length,
        },
        METHOD_SIGNATURE
      );

      publishEvent(
        CONSTANTS.EVENTS.SELECTION_AUTO_APPLY_COMPLETED,
        {
          categoryId: request.categoryId,
          variantId: request.variantId,
          productId: productId,
          auto_applied: true,
        },
        METHOD_SIGNATURE
      );

      trackPerformance('SINGLE_STEP', stepDuration, {
        categoryId: request.categoryId,
        stepNumber: cascadeDepth,
      });
    } catch (error) {
      isDev() &&
        console.error('[' + METHOD_SIGNATURE + '] Step failed:', error);

      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_FAILED,
        {
          error: error.message,
          stack: error.stack,
          categoryId: request.categoryId,
          variantId: request.variantId,
          stepNumber: cascadeDepth,
          cascadeSessionId: cascadeSessionId,
          source: METHOD_SIGNATURE,
        },
        METHOD_SIGNATURE
      );
    } finally {
      // Release lock and process next item
      cascadeInProgress = false;

      // Use setTimeout(0) to yield to the event loop, allowing any new
      // auto_apply_requests (triggered by our APPLY_SELECTION) to arrive
      // before we check the queue again
      setTimeout(function () {
        processCascadeQueue(conductor);
      }, 0);
    }
  }

  // ============================================================================
  // HELPER: resolveProductId
  // ============================================================================
  /**
   * Resolves productId from variantId via conductor delegation (PPD-first).
   *
   * PATTERN 7: Perfect Dictionary Only
   * PATTERN 8: No Raw Data Access
   *
   * @param {string|number} variantId - The variant ID to resolve
   * @param {Object} conductor - The central conductor
   * @returns {string|null} productId or null
   */
  function resolveProductId(variantId, conductor) {
    try {
      if (!conductor || typeof conductor.getProductByVariantId !== 'function') {
        isDev() &&
          console.error(
            '[CascadeAutoSelectionCoordinator] Conductor missing getProductByVariantId method'
          );
        return null;
      }

      var product = conductor.getProductByVariantId(variantId);
      // Prefer productId (composite string key like "composite-bottom-pocket-standard-shelves")
      // over id (numeric raw Shopify ID) for virtualized/composite products.
      // Regular Shopify products don't have productId, so they fall back to id correctly.
      return product ? product.productId || product.id || null : null;
    } catch (error) {
      isDev() &&
        console.error(
          '[CascadeAutoSelectionCoordinator] Error resolving productId:',
          error
        );
      return null;
    }
  }

  // ============================================================================
  // HELPER: scheduleTermination
  // ============================================================================
  /**
   * Schedules cascade termination after debounce window.
   * If no new auto_apply_requests arrive within the window, cascade is complete.
   */
  function scheduleTermination() {
    if (terminationTimer) {
      clearTimeout(terminationTimer);
    }

    terminationTimer = setTimeout(function () {
      terminationTimer = null;

      // Publish completion if we did cascade steps OR if forbidden auto-apply
      // populated autoAppliedCategories without incrementing cascadeDepth.
      // Without the autoAppliedCategories check, the forbidden AUTO_APPLY_OPTION
      // path silently skips this event and Safety Net 2 in image-layer-coordinator
      // (handleCascadeCompleted) never fires.
      if (cascadeDepth > 0 || autoAppliedCategories.size > 0) {
        var totalDuration =
          (window.performance ? window.performance.now() : Date.now()) -
          cascadeStartTime;

        // STAGE 4: CASCADE COMPLETED
        publishEvent(
          CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_COMPLETED,
          {
            cascadeSessionId: cascadeSessionId,
            totalSteps: cascadeDepth,
            totalDuration: parseFloat(totalDuration.toFixed(2)),
            autoAppliedCategories: Array.from(autoAppliedCategories.keys()),
            manualSelectionsProtected: Array.from(manualSelections.keys()),
          },
          'scheduleTermination'
        );

        trackPerformance('TOTAL_CASCADE', totalDuration, {
          totalSteps: cascadeDepth,
          sessionId: cascadeSessionId,
        });

        isDev() &&
          console.log(
            '[CascadeAutoSelectionCoordinator] CASCADE COMPLETED (session: ' +
              cascadeSessionId +
              ', steps: ' +
              cascadeDepth +
              ', duration: ' +
              totalDuration.toFixed(2) +
              'ms)'
          );
      }

      // Reset cascade state for next cascade
      cascadeDepth = 0;
      cascadeQueue = [];
    }, CASCADE_CONFIG.TERMINATION_DEBOUNCE_MS);
  }

  // ============================================================================
  // MANUAL SELECTION TRACKING
  // ============================================================================
  /**
   * Handles selection:variant_applied events to track manual selections.
   * Manual selections (not from this module) are sacred and protected.
   *
   * PATTERN 4: Event Consumer Handlers
   * PATTERN 17: XSS Prevention - validates event data types
   *
   * @param {Object} eventData - Event payload from selection:variant_applied
   */
  function handleVariantApplied(eventData) {
    try {
      var detail = eventData.detail || eventData;
      var categoryId = detail.categoryId;
      var variantId = detail.variantId;

      if (!categoryId) return;

      // Use the auto_applied flag from the event payload (set by conductor from data.auto_applied).
      // This is the authoritative source - it's set BEFORE the event is published, eliminating
      // the race condition where autoAppliedCategories Map hadn't been populated yet.
      if (detail.auto_applied === true) {
        // An enforced-pair hold only means "empty until chosen". If some path
        // did fill the category, the hold has no meaning left; drop it so it
        // does not masquerade as a manual pick.
        var held = manualSelections.get(categoryId);
        if (held && held.enforcedPairHold === true) {
          manualSelections.delete(categoryId);
        }
        isDev() &&
          console.log(
            '[CascadeAutoSelectionCoordinator] Confirmed auto-apply for category "' +
              categoryId +
              '" (variant: ' +
              variantId +
              ')'
          );
        return;
      }

      // This is a manual user selection - record it!
      manualSelections.set(categoryId, {
        variantId: variantId,
        timestamp: Date.now(),
      });

      // If we previously auto-applied to this category, remove our record
      // (user has overridden our auto-apply)
      if (autoAppliedCategories.has(categoryId)) {
        isDev() &&
          console.log(
            '[CascadeAutoSelectionCoordinator] Manual selection OVERRIDES auto-apply for category "' +
              categoryId +
              '"'
          );
        autoAppliedCategories.delete(categoryId);
      }

      isDev() &&
        console.log(
          '[CascadeAutoSelectionCoordinator] Tracked manual selection: category="' +
            categoryId +
            '" variant=' +
            variantId
        );
    } catch (error) {
      isDev() &&
        console.error(
          '[CascadeAutoSelectionCoordinator] handleVariantApplied failed:',
          error
        );
    }
  }

  // ============================================================================
  // BUILD PRESET TRANSPORT HELPERS
  // ============================================================================
  // The context md-config-support-build-preset.js publishes on
  // BUSINESS_CASCADE_APPLY_URL_SELECTIONS. Anything else on that event (the
  // parser's 'url_restoration', the editor bridge's 'editor_bridge_restore')
  // keeps the historical 'url_restoration' provenance.
  // Provenance stamping only. Which contexts count as a RESTORE of the
  // customer's own picks is CONSTANTS.CASCADE_RESTORE_CONTEXTS (read by
  // md-config-bl-gate-card-coordinator.js); keep the two in step.
  var BUILD_PRESET_CONTEXTS = Object.freeze({
    build_preset: true,
  });

  function isBuildPresetContext(context) {
    return (
      typeof context === 'string' && BUILD_PRESET_CONTEXTS[context] === true
    );
  }

  /**
   * Provenance for a BUSINESS_CASCADE_APPLY_URL_SELECTIONS publish: the
   * build_preset contexts stamp themselves, everything else stays
   * 'url_restoration' exactly as before.
   * @param {Object} eventData
   * @returns {string}
   */
  function resolveUrlSelectionsSource(eventData) {
    var detail = (eventData && (eventData.detail || eventData)) || {};
    return isBuildPresetContext(detail.context)
      ? detail.context
      : 'url_restoration';
  }

  /**
   * Adds `requestId` to a payload ONLY when the caller supplied one, so the
   * legacy publishers' payloads stay byte-for-byte identical.
   * @param {Object} payload
   * @param {string|null} requestId
   * @returns {Object}
   */
  function withRequestId(payload, requestId) {
    if (requestId) payload.requestId = requestId;
    return payload;
  }

  // ============================================================================
  // URL RESTORATION HANDLER
  // ============================================================================
  /**
   * Applies a batch of already-validated selections through the existing
   * cascade logic via conductor delegation (APPLY_SELECTION).
   *
   * Serves TWO publishers, both of which hand over selections some other
   * module already validated against the PPD:
   *   BUSINESS_CASCADE_APPLY_URL_SELECTIONS       <- url-param-parser (?config=)
   *   BUSINESS_CASCADE_APPLY_WILLITFIT_SELECTIONS <- willitfit-prefill
   *
   * They are separate events for ONE reason: provenance. Every applied
   * selection is stamped with `restorationSource`, so state, the cascade step
   * events and the failure event all say which source produced it. Folding
   * them into one event would relabel every wizard prefill as a URL
   * restoration and make that trail useless. The apply mechanics are identical
   * and deliberately live in one function.
   *
   * PATTERN 4: Event Consumer Handler
   * PATTERN 7: PPD-First via conductor delegation
   * PATTERN 14: Try/Catch + Failed Events
   *
   * @param {Object} eventData - Event payload with selections array
   * @param {Object} conductor - The central conductor for delegation
   * @param {string} [source] - Provenance label stamped on every applied selection
   */
  function handleUrlRestorationRequest(eventData, conductor, source) {
    var METHOD_SIGNATURE =
      'CascadeAutoSelectionCoordinator.handleUrlRestorationRequest';
    var restorationSource = source || 'url_restoration';
    var startTime = window.performance ? window.performance.now() : Date.now();

    // "Build this" presets (md-config-support-build-preset.js) ride this same
    // transport with three OPTIONAL fields. When all three are absent (every
    // url_restoration / willitfit_prefill / editor_bridge publish) nothing
    // below behaves differently -- pinned by build-preset-coordinator.test.js.
    //   requestId        echoed on COMPLETED/FAILED so the caller can tell its
    //                    own terminal event from someone else's; the early
    //                    returns below answer on APPLY_REQUEST_REJECTED
    //   build_preset     the picks apply EXACTLY like a ?config= restore
    //   contexts         (auto_applied:true, tracked in autoAppliedCategories
    //                    with reason 'build_preset', provenance 'url'), so a
    //                    later Wood/Finish change re-resolves them through the
    //                    single-compatible auto-apply and the material remap.
    //                    Before each pick lands, any earlier manualSelections
    //                    entry for its category is dropped: the preset replaces
    //                    that pick, and a stale entry would keep guard 1 of the
    //                    auto-apply skipping the category forever. Picks the
    //                    customer makes by hand AFTER the preset are recorded
    //                    as manual again by handleVariantApplied. (The retired
    //                    `preserveAsManual` flag is ignored.)
    var requestDetail = (eventData && (eventData.detail || eventData)) || {};
    var requestId =
      typeof requestDetail.requestId === 'string' && requestDetail.requestId
        ? requestDetail.requestId
        : null;
    var isBuildPresetRequest = isBuildPresetContext(restorationSource);
    // Only callers that asked for an answer get one on the early returns, so
    // the legacy publishers keep their exact event stream.
    var answerEarlyReturns = requestId !== null || isBuildPresetRequest;

    // Acquire cascadeInProgress lock to prevent concurrent cascades from
    // corrupting shared state (cascadeDepth, autoAppliedCategories, etc.)
    if (cascadeInProgress) {
      isDev() &&
        console.warn(
          '[' +
            METHOD_SIGNATURE +
            '] A cascade is already in progress. URL restoration will be skipped.'
        );
      if (answerEarlyReturns) {
        // REJECTED, never FAILED: no STARTED was published for this request,
        // and FAILED subscribers (cart-manager, ui-updater) would read it as
        // the end of the cascade that IS running and unlock add-to-cart.
        publishEvent(
          CONSTANTS.EVENTS.BUSINESS_CASCADE_APPLY_REQUEST_REJECTED,
          withRequestId(
            {
              error: 'cascade_in_progress',
              reason: 'cascade_in_progress',
              retryable: true,
              cascadeSessionId: cascadeSessionId,
              context: restorationSource,
              source: METHOD_SIGNATURE,
            },
            requestId
          ),
          METHOD_SIGNATURE
        );
      }
      return;
    }

    cascadeInProgress = true;

    try {
      var detail = eventData.detail || eventData;
      var selections = detail.selections;

      if (
        !selections ||
        !Array.isArray(selections) ||
        selections.length === 0
      ) {
        isDev() &&
          console.warn(
            '[' +
              METHOD_SIGNATURE +
              '] No valid selections in URL restoration request'
          );
        if (answerEarlyReturns) {
          // A caller waiting on its requestId must never hang on an empty batch.
          // REJECTED (not FAILED): no cascade STARTED, so nothing ended.
          publishEvent(
            CONSTANTS.EVENTS.BUSINESS_CASCADE_APPLY_REQUEST_REJECTED,
            withRequestId(
              {
                error: 'no_selections',
                reason: 'no_selections',
                retryable: false,
                cascadeSessionId: cascadeSessionId,
                context: restorationSource,
                source: METHOD_SIGNATURE,
              },
              requestId
            ),
            METHOD_SIGNATURE
          );
        }
        return;
      }

      isDev() &&
        console.log(
          '[' +
            METHOD_SIGNATURE +
            '] Applying ' +
            selections.length +
            ' URL selections via cascade logic'
        );

      // Start a new cascade session for URL restoration
      cascadeSessionId++;
      cascadeStartTime = startTime;

      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_STARTED,
        {
          sessionId: cascadeSessionId,
          triggerSource: restorationSource,
          selectionCount: selections.length,
          context: detail.context || restorationSource,
        },
        METHOD_SIGNATURE
      );

      // Apply each selection sequentially through conductor delegation
      for (var i = 0; i < selections.length; i++) {
        var selection = selections[i];

        try {
          // PATTERN 17: XSS Prevention - validate selection data types
          var categoryId =
            typeof selection.categoryId === 'string'
              ? selection.categoryId
              : String(selection.categoryId || '');
          var productId = selection.productId
            ? String(selection.productId)
            : null;
          var variantId = selection.variantId
            ? String(selection.variantId)
            : null;

          if (!categoryId || (!productId && !variantId)) {
            isDev() &&
              console.warn(
                '[' +
                  METHOD_SIGNATURE +
                  '] Skipping invalid selection at index ' +
                  i,
                selection
              );
            continue;
          }

          // Resolve productId from variantId via conductor if not provided
          if (!productId && variantId) {
            productId = resolveProductId(variantId, conductor);
          }

          if (!productId) {
            isDev() &&
              console.warn(
                '[' +
                  METHOD_SIGNATURE +
                  '] Could not resolve productId for selection at index ' +
                  i
              );
            continue;
          }

          isDev() &&
            console.log(
              '[' +
                METHOD_SIGNATURE +
                '] Applying URL selection ' +
                (i + 1) +
                '/' +
                selections.length +
                ': category="' +
                categoryId +
                '" product=' +
                productId +
                ' variant=' +
                variantId
            );

          // A build preset replaces whatever the customer had picked in this
          // category; drop the stale manual record so guard 1 of the
          // single-compatible auto-apply lets the preset pick re-resolve on a
          // later Wood/Finish change, exactly like a ?config= restore.
          if (isBuildPresetRequest && manualSelections.has(categoryId)) {
            manualSelections.delete(categoryId);
          }

          // Apply via conductor delegation - same as cascade auto-selection
          // Using auto_applied: true so the manual selection tracker ignores these
          // Using isOneStep: true for immediate application without browsing state
          conductor.handleStateChange('APPLY_SELECTION', {
            categoryId: categoryId,
            productId: productId,
            variantId: variantId,
            auto_applied: true,
            isOneStep: true,
            source: restorationSource,
          });

          // Track this auto-apply (reason included for stale detection filtering)
          autoAppliedCategories.set(categoryId, {
            variantId: variantId,
            timestamp: Date.now(),
            reason: restorationSource,
          });

          cascadeDepth++;

          // Publish step event
          publishEvent(
            CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_STEP,
            {
              categoryId: categoryId,
              productId: productId,
              variantId: variantId,
              stepNumber: i + 1,
              cascadeSessionId: cascadeSessionId,
              reason: restorationSource,
              queueRemaining: selections.length - i - 1,
            },
            METHOD_SIGNATURE
          );
        } catch (stepError) {
          isDev() &&
            console.error(
              '[' +
                METHOD_SIGNATURE +
                '] Failed to apply selection at index ' +
                i +
                ':',
              stepError
            );
          // Continue with remaining selections even if one fails
        }
      }

      // Publish cascade completion for URL restoration
      var totalDuration =
        (window.performance ? window.performance.now() : Date.now()) -
        startTime;

      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_COMPLETED,
        withRequestId(
          {
            cascadeSessionId: cascadeSessionId,
            totalSteps: cascadeDepth,
            totalDuration: parseFloat(totalDuration.toFixed(2)),
            context: restorationSource,
            autoAppliedCategories: Array.from(autoAppliedCategories.keys()),
          },
          requestId
        ),
        METHOD_SIGNATURE
      );

      trackPerformance('TOTAL_CASCADE', totalDuration, {
        totalSteps: cascadeDepth,
        sessionId: cascadeSessionId,
        context: restorationSource,
      });

      isDev() &&
        console.log(
          '[' +
            METHOD_SIGNATURE +
            '] URL RESTORATION COMPLETE (session: ' +
            cascadeSessionId +
            ', steps: ' +
            cascadeDepth +
            ', duration: ' +
            totalDuration.toFixed(2) +
            'ms)'
        );
    } catch (error) {
      isDev() &&
        console.error(
          '[' + METHOD_SIGNATURE + '] URL restoration failed:',
          error
        );

      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_FAILED,
        withRequestId(
          {
            error: error.message,
            stack: error.stack,
            cascadeSessionId: cascadeSessionId,
            context: restorationSource,
            source: METHOD_SIGNATURE,
          },
          requestId
        ),
        METHOD_SIGNATURE
      );
    } finally {
      // Reset cascade depth for this flow
      cascadeDepth = 0;
      // DO NOT clear cascadeQueue - it may contain auto-apply requests that were
      // legitimately triggered by the selections applied in the loop above.

      // Release the lock
      cascadeInProgress = false;

      // After releasing the lock, process any items that were queued during
      // the synchronous URL restoration loop (e.g., auto-selections triggered
      // by the applied selections).
      if (cascadeQueue.length > 0) {
        isDev() &&
          console.log(
            '[' +
              METHOD_SIGNATURE +
              '] Processing ' +
              cascadeQueue.length +
              ' auto-apply requests queued during URL restoration'
          );
        processCascadeQueue(conductor);
      }
    }
  }

  // ============================================================================
  // SIMPLIFIED MODE: Apply 17 pre-built defaults after 3 user selections
  // ============================================================================
  /**
   * Handles BUSINESS_CASCADE_APPLY_SIMPLIFIED_DEFAULTS.
   * Mirrors handleUrlRestorationRequest exactly — direct publish with
   * context: 'simplified_mode', bypassing scheduleTermination() so the
   * simplified-mode-coordinator can distinguish this completion.
   *
   * @param {Object} eventData - Event payload with selections array
   * @param {Object} conductor - The central conductor for delegation
   */
  /**
   * True while `categoryId` is held empty by an enforced-pair reverse clear
   * (see handleEnforcedPairClearTriggered). Read-only; exposed on the public
   * API and via conductor.isEnforcedPairHeld() for other modules.
   */
  function isEnforcedPairHeld(categoryId) {
    var entry = categoryId ? manualSelections.get(categoryId) : null;
    return !!(entry && entry.enforcedPairHold === true);
  }

  function handleSimplifiedDefaultsRequest(eventData, conductor) {
    var METHOD_SIGNATURE =
      'CascadeAutoSelectionCoordinator.handleSimplifiedDefaultsRequest';
    var startTime = window.performance ? window.performance.now() : Date.now();

    // C2: this bulk-defaults handler now serves both the simplified 3Q flow
    // (context 'simplified_mode') and the curated load-time defaults
    // (context 'curated_defaults'). The context drives the APPLY_SELECTION
    // source/provenance and, for curated, the BUSINESS_CURATED_DEFAULTS_APPLIED
    // signal the URL restorer gates on (must fire on EVERY exit path).
    var _bulkDetail = eventData ? eventData.detail || eventData : {};
    var bulkContext = _bulkDetail.context || 'simplified_mode';
    var _curatedSignalled = false;
    function signalCuratedIfNeeded() {
      if (bulkContext === 'curated_defaults' && !_curatedSignalled) {
        _curatedSignalled = true;
        // Global latch so a late URL-restorer that missed the event (curated
        // applies synchronously on the same bootstrap tick) can still detect
        // completion and proceed without hanging.
        window.MurphyDoorConfigurator._curatedDefaultsApplied = true;
        publishEvent(
          CONSTANTS.EVENTS.BUSINESS_CURATED_DEFAULTS_APPLIED,
          {
            context: 'curated_defaults',
            timestamp: Date.now(),
            source: METHOD_SIGNATURE,
          },
          METHOD_SIGNATURE
        );
      }
    }

    if (cascadeInProgress) {
      isDev() &&
        console.warn(
          '[' +
            METHOD_SIGNATURE +
            '] A cascade is already in progress. Bulk defaults (' +
            bulkContext +
            ') DROPPED -- the publisher must recompute after the cascade completes.'
        );
      // Nothing here retries the batch, and a blind replay later would apply a
      // stale payload over newer state. Say so, so a publisher that cares can
      // recompute from live state on the next completion
      // (md-config-bl-selection-dependency-coordinator.js does). REJECTED, never
      // FAILED: FAILED subscribers would read it as the end of the cascade that
      // IS running and unlock add-to-cart (see the URL-restoration path above).
      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_CASCADE_APPLY_REQUEST_REJECTED,
        {
          error: 'cascade_in_progress',
          reason: 'cascade_in_progress',
          retryable: true,
          cascadeSessionId: cascadeSessionId,
          context: bulkContext,
          source: METHOD_SIGNATURE,
        }
      );
      // Still signal for curated so the URL restorer is never left waiting.
      signalCuratedIfNeeded();
      return;
    }

    cascadeInProgress = true;

    try {
      var detail = eventData.detail || eventData;
      var selections = detail.selections;

      if (
        !selections ||
        !Array.isArray(selections) ||
        selections.length === 0
      ) {
        isDev() &&
          console.warn(
            '[' +
              METHOD_SIGNATURE +
              '] No valid selections in simplified defaults request'
          );
        return;
      }

      isDev() &&
        console.log(
          '[' +
            METHOD_SIGNATURE +
            '] Applying ' +
            selections.length +
            ' simplified defaults via cascade logic'
        );

      cascadeSessionId++;
      cascadeStartTime = startTime;

      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_STARTED,
        {
          sessionId: cascadeSessionId,
          triggerSource: 'simplified_mode',
          selectionCount: selections.length,
          context: 'simplified_mode',
        },
        METHOD_SIGNATURE
      );

      for (var i = 0; i < selections.length; i++) {
        var selection = selections[i];

        try {
          var categoryId =
            typeof selection.categoryId === 'string'
              ? selection.categoryId
              : String(selection.categoryId || '');
          var productId = selection.productId
            ? String(selection.productId)
            : null;
          var variantId = selection.variantId
            ? String(selection.variantId)
            : null;

          if (!categoryId || (!productId && !variantId)) {
            isDev() &&
              console.warn(
                '[' +
                  METHOD_SIGNATURE +
                  '] Skipping invalid selection at index ' +
                  i,
                selection
              );
            continue;
          }

          // ENFORCED_PAIR HOLD: this handler is the single choke point for every
          // bulk default (curated load + cascade re-default, simplified 3Q,
          // gate defaults). A category emptied by an enforced-pair clear must
          // stay empty until the CUSTOMER chooses; a default landing there would
          // silently re-enable Add to Cart with a choice nobody made. The rest
          // of the payload applies unchanged. (No hold can exist at page load,
          // so the initial curated defaults are unaffected.)
          if (isEnforcedPairHeld(categoryId)) {
            isDev() &&
              console.log(
                '[' +
                  METHOD_SIGNATURE +
                  '] Skipping ' +
                  bulkContext +
                  ' default for "' +
                  categoryId +
                  '" - held empty by an enforced pair until the customer chooses'
              );
            continue;
          }

          if (!productId && variantId) {
            productId = resolveProductId(variantId, conductor);
          }

          if (!productId) {
            isDev() &&
              console.warn(
                '[' +
                  METHOD_SIGNATURE +
                  '] Could not resolve productId for selection at index ' +
                  i
              );
            continue;
          }

          isDev() &&
            console.log(
              '[' +
                METHOD_SIGNATURE +
                '] Applying simplified default ' +
                (i + 1) +
                '/' +
                selections.length +
                ': category="' +
                categoryId +
                '" product=' +
                productId +
                ' variant=' +
                variantId
            );

          conductor.handleStateChange('APPLY_SELECTION', {
            categoryId: categoryId,
            productId: productId,
            variantId: variantId,
            auto_applied: true,
            isOneStep: true,
            source: bulkContext,
          });

          autoAppliedCategories.set(categoryId, {
            variantId: variantId,
            timestamp: Date.now(),
            reason: bulkContext,
          });

          cascadeDepth++;

          publishEvent(
            CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_STEP,
            {
              categoryId: categoryId,
              productId: productId,
              variantId: variantId,
              stepNumber: i + 1,
              cascadeSessionId: cascadeSessionId,
              reason: 'simplified_mode',
              queueRemaining: selections.length - i - 1,
            },
            METHOD_SIGNATURE
          );
        } catch (stepError) {
          isDev() &&
            console.error(
              '[' +
                METHOD_SIGNATURE +
                '] Failed to apply simplified default at index ' +
                i +
                ':',
              stepError
            );
        }
      }

      var totalDuration =
        (window.performance ? window.performance.now() : Date.now()) -
        startTime;

      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_COMPLETED,
        {
          cascadeSessionId: cascadeSessionId,
          totalSteps: cascadeDepth,
          totalDuration: parseFloat(totalDuration.toFixed(2)),
          context: 'simplified_mode',
          autoAppliedCategories: Array.from(autoAppliedCategories.keys()),
        },
        METHOD_SIGNATURE
      );

      trackPerformance('TOTAL_CASCADE', totalDuration, {
        totalSteps: cascadeDepth,
        sessionId: cascadeSessionId,
        context: 'simplified_mode',
      });

      isDev() &&
        console.log(
          '[' +
            METHOD_SIGNATURE +
            '] SIMPLIFIED DEFAULTS APPLIED (session: ' +
            cascadeSessionId +
            ', steps: ' +
            cascadeDepth +
            ', duration: ' +
            totalDuration.toFixed(2) +
            'ms)'
        );
    } catch (error) {
      isDev() &&
        console.error(
          '[' + METHOD_SIGNATURE + '] Simplified defaults application failed:',
          error
        );

      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_FAILED,
        {
          error: error.message,
          stack: error.stack,
          cascadeSessionId: cascadeSessionId,
          context: 'simplified_mode',
          source: METHOD_SIGNATURE,
        },
        METHOD_SIGNATURE
      );
    } finally {
      cascadeDepth = 0;
      cascadeInProgress = false;

      // C2: signal curated completion on the normal + error exit paths so the
      // URL restorer can merge shared-config selections over the defaults.
      signalCuratedIfNeeded();

      if (cascadeQueue.length > 0) {
        isDev() &&
          console.log(
            '[' +
              METHOD_SIGNATURE +
              '] Processing ' +
              cascadeQueue.length +
              ' auto-apply requests queued during bulk defaults application'
          );
        processCascadeQueue(conductor);
      }
    }
  }

  // ============================================================================
  // AUTO_APPLY_OPTION: Circular Dependency Detection
  // ============================================================================
  /**
   * Detect if an auto-apply would create a circular dependency.
   * Prevents infinite loops like: A -> B -> A -> B -> ...
   *
   * Checks:
   * 1. Direct bidirectional loop: sourceVariant -> targetVariant already has
   *    targetVariant -> sourceVariant in the chain
   * 2. Multi-hop chain: walks the autoApplyChains map to detect cycles
   * 3. Max depth safety valve
   *
   * PATTERN 14: Try/Catch + Failed Events
   *
   * @param {string} sourceVariantId - The variant that triggered the auto-apply
   * @param {string} targetVariantId - The variant that would be auto-applied
   * @param {number} [depth=0] - Current recursion depth
   * @returns {boolean} True if circular dependency detected (BLOCK the apply!)
   */
  function detectCircularAutoApply(sourceVariantId, targetVariantId, depth) {
    depth = depth || 0;

    try {
      // Safety valve: max depth exceeded
      if (depth > MAX_AUTO_APPLY_CHAIN_DEPTH) {
        isDev() &&
          console.warn(
            '[CascadeAutoSelectionCoordinator] CIRCULAR: Max chain depth (' +
              MAX_AUTO_APPLY_CHAIN_DEPTH +
              ') exceeded',
            {
              sourceVariantId: sourceVariantId,
              targetVariantId: targetVariantId,
              depth: depth,
            }
          );
        return true;
      }

      // Check 1: Direct bidirectional loop (target -> source already exists)
      var existingChain = autoApplyChains.get(String(sourceVariantId));
      if (
        existingChain &&
        String(existingChain.sourceVariantId) === String(targetVariantId)
      ) {
        isDev() &&
          console.warn(
            '[CascadeAutoSelectionCoordinator] CIRCULAR: Direct bidirectional loop detected',
            {
              source: sourceVariantId,
              target: targetVariantId,
              existingChain: existingChain,
            }
          );
        return true;
      }

      // Check 2: Multi-hop cycle detection - walk from sourceVariantId up its ancestry chain
      // to check if targetVariantId is already an ancestor (would create A->B->C->A cycle)
      var current = String(sourceVariantId);
      var visitedInThisCheck = new Set();
      var checkDepth = 0;

      while (
        current &&
        !visitedInThisCheck.has(current) &&
        checkDepth < MAX_AUTO_APPLY_CHAIN_DEPTH
      ) {
        if (current === String(targetVariantId)) {
          // Cycle detected
          isDev() &&
            console.warn(
              '[CascadeAutoSelectionCoordinator] CIRCULAR: Multi-hop cycle detected',
              {
                source: sourceVariantId,
                target: targetVariantId,
                path: Array.from(visitedInThisCheck),
              }
            );
          return true;
        }

        visitedInThisCheck.add(current);
        var chain = autoApplyChains.get(current);
        if (!chain) {
          break;
        }
        current = String(chain.sourceVariantId);
        checkDepth++;
      }

      return false;
    } catch (error) {
      isDev() &&
        console.error(
          '[CascadeAutoSelectionCoordinator] CIRCULAR: Detection error:',
          error
        );

      // Publish observable failure event for monitoring (not just silent console.error)
      publishEvent(
        CONSTANTS.EVENTS.FORBIDDEN_AUTO_APPLY_FAILED,
        {
          reason: 'circular_dependency_detection_error',
          error: error.message,
          stack: error.stack,
          sourceVariantId: sourceVariantId,
          targetVariantId: targetVariantId,
          source: 'CascadeAutoSelectionCoordinator.detectCircularAutoApply',
        },
        'CascadeAutoSelectionCoordinator.detectCircularAutoApply'
      );

      // On error, assume circular to be safe (block the apply)
      return true;
    }
  }

  // ============================================================================
  // AUTO_APPLY_OPTION: Handle auto-apply triggered event
  // ============================================================================
  /**
   * Handles forbidden:auto_apply_triggered events.
   * Validates the request, checks for circular dependencies, tracks the rule,
   * and delegates to the conductor to actually apply the selection.
   *
   * IMPLEMENTATION NOTE - SINGLE-LEVEL AUTO-APPLY (Current Behavior):
   *   The current implementation supports single-level auto-apply only. When a user
   *   selects variant A and a forbidden rule says "auto-apply variant B", variant B
   *   is applied but does NOT trigger further auto-apply evaluation for variant B's
   *   own forbidden rules. This is intentional and enforced by two safeguards:
   *
   *   1. CONDUCTOR GUARD (md-config-core-conductor.js ~line 3525):
   *      The conductor skips auto-apply evaluation when `data.auto_applied === true`.
   *      This prevents the conductor from re-entering the auto-apply evaluation loop
   *      for selections that were themselves auto-applied. This is the PRIMARY guard
   *      that prevents cascading, and exists for both YAGNI and efficiency reasons.
   *
   *   2. ACTIVE RULES DEDUP (this file, SAFEGUARD 2):
   *      If the same ruleId fires twice (e.g., duplicate events), we skip it.
   *      This is a secondary defense against duplicate processing.
   *
   * ARCHITECTURE READINESS - MULTI-LEVEL CASCADING (Future):
   *   The architecture IS ready for multi-level cascading if the business need arises.
   *   This module already has circular dependency detection (detectCircularAutoApply),
   *   chain depth tracking (autoApplyChains), and per-category dedup (autoAppliedCategories).
   *   To enable multi-level cascading:
   *     1. Remove the conductor guard (`data.auto_applied !== true` check)
   *     2. Add MAX_CASCADE_DEPTH constant and enforce it in this handler
   *     3. Write comprehensive UAT tests covering A->B->C chains and circular scenarios
   *     4. Add telemetry/logging for cascade depth in production
   *   DO NOT enable without thorough testing - infinite loops in production are bad news.
   *
   * PATTERN 3: 5-Stage Event Lifecycle
   * PATTERN 14: Try/Catch + Failed Events
   * PATTERN 18: Frozen State Objects (via publishEvent)
   *
   * @param {Object} eventData - Event payload from forbidden:auto_apply_triggered
   * @param {Object} conductor - The central conductor for delegation
   */
  function handleForbiddenAutoApplyTriggered(eventData, conductor) {
    var METHOD_SIGNATURE =
      'CascadeAutoSelectionCoordinator.handleForbiddenAutoApplyTriggered';
    var startTime = window.performance ? window.performance.now() : Date.now();

    try {
      var detail = eventData.detail || eventData;

      // STAGE 2: VALIDATION
      // PATTERN 17: XSS Prevention - validate data types
      // Development-only: Fail loudly for missing required fields to catch bugs early
      if (isDev()) {
        if (!detail.ruleId) {
          console.error(
            '[' +
              METHOD_SIGNATURE +
              '] VALIDATION ERROR: Missing ruleId in event payload',
            detail
          );
        }
        if (!detail.targetVariantId) {
          console.error(
            '[' +
              METHOD_SIGNATURE +
              '] VALIDATION ERROR: Missing targetVariantId in event payload',
            detail
          );
        }
        if (!detail.conditionVariantId) {
          console.error(
            '[' +
              METHOD_SIGNATURE +
              '] VALIDATION ERROR: Missing conditionVariantId in event payload',
            detail
          );
        }
      }

      var ruleId =
        typeof detail.ruleId === 'string'
          ? detail.ruleId
          : String(detail.ruleId || '');
      var conditionVariantId =
        typeof detail.conditionVariantId === 'string'
          ? detail.conditionVariantId
          : String(detail.conditionVariantId || '');
      var targetVariantId =
        typeof detail.targetVariantId === 'string'
          ? detail.targetVariantId
          : String(detail.targetVariantId || '');
      var targetCategoryId = detail.targetCategoryId || null;
      var targetProductId = detail.targetProductId || null;
      // ENFORCED_PAIR (opt-in via rule.conflict_direction): the validator already
      // decided the target must be applied, so the "fill only untouched
      // categories" guards below are bypassed for it. Every other rule keeps the
      // original behavior exactly.
      var enforcedPair = detail.enforcedPair === true;

      if (!ruleId || !targetVariantId) {
        isDev() &&
          console.warn(
            '[' +
              METHOD_SIGNATURE +
              '] Invalid auto_apply event: missing ruleId or targetVariantId',
            { ruleId: ruleId, targetVariantId: targetVariantId }
          );
        return;
      }

      isDev() &&
        console.log('[' + METHOD_SIGNATURE + '] Processing AUTO_APPLY_OPTION', {
          ruleId: ruleId,
          conditionVariantId: conditionVariantId,
          targetVariantId: targetVariantId,
          targetCategoryId: targetCategoryId,
        });

      // GUARD: Skip if target category already has an auto-applied selection from this system
      // (ENFORCED_PAIR overrides it: the evaluator only fires when the target is NOT applied)
      if (
        !enforcedPair &&
        targetCategoryId &&
        autoAppliedCategories.has(targetCategoryId)
      ) {
        isDev() &&
          console.log(
            '[' +
              METHOD_SIGNATURE +
              '] SKIPPED - target category "' +
              targetCategoryId +
              '" already has an auto-applied selection from a forbidden rule.'
          );
        return;
      }

      // ENFORCED_PAIR: an entry for this rule can be stale (its target was
      // replaced or cleared without a STATE_SELECTION_CLEARED for the condition).
      // The evaluator only emits when the target is not currently applied, so a
      // re-fire is legitimate: drop the stale tracking and its chain link first.
      if (enforcedPair && activeAutoApplyRules.has(ruleId)) {
        var staleRuleState = activeAutoApplyRules.get(ruleId);
        activeAutoApplyRules.delete(ruleId);
        if (staleRuleState && staleRuleState.targetVariantId) {
          autoApplyChains.delete(String(staleRuleState.targetVariantId));
        }
      }
      if (enforcedPair) {
        // A chain link whose rule is no longer active is history, not a cycle.
        var conditionChain = autoApplyChains.get(String(conditionVariantId));
        if (
          conditionChain &&
          !activeAutoApplyRules.has(conditionChain.ruleId)
        ) {
          autoApplyChains.delete(String(conditionVariantId));
        }
        // Chains are keyed by the variant a rule applied. Once that variant or
        // the variant that caused it is no longer applied (replaced or cleared
        // without a tracked unapply, e.g. by a restore), the link is stale and
        // would make a legitimate re-force look circular.
        pruneStaleChains(conductor);
      }

      // SAFEGUARD 2: Check if this rule is already active (duplicate prevention)
      if (activeAutoApplyRules.has(ruleId)) {
        var existingRule = activeAutoApplyRules.get(ruleId);
        isDev() &&
          console.log(
            '[' +
              METHOD_SIGNATURE +
              '] SKIPPED - rule "' +
              ruleId +
              '" already active (target: ' +
              existingRule.targetVariantId +
              ')'
          );
        return;
      }

      // SAFEGUARD 3: Check for circular dependency
      if (detectCircularAutoApply(conditionVariantId, targetVariantId)) {
        isDev() &&
          console.warn(
            '[' + METHOD_SIGNATURE + '] BLOCKED - circular auto-apply detected',
            {
              source: conditionVariantId,
              target: targetVariantId,
              ruleId: ruleId,
            }
          );

        publishEvent(
          CONSTANTS.EVENTS.FORBIDDEN_AUTO_APPLY_FAILED,
          {
            ruleId: ruleId,
            conditionVariantId: conditionVariantId,
            targetVariantId: targetVariantId,
            reason: 'circular_dependency_detected',
            source: METHOD_SIGNATURE,
          },
          METHOD_SIGNATURE
        );

        return;
      }

      // GUARD: Skip if target category has manual selection (user intent is sacred!)
      // ENFORCED_PAIR is a hard business rule and goes ahead over a manual pick;
      // the manual tracking is only released once the forced apply has landed.
      if (
        !enforcedPair &&
        targetCategoryId &&
        manualSelections.has(targetCategoryId)
      ) {
        var manualInfo = manualSelections.get(targetCategoryId);
        isDev() &&
          console.log(
            '[' +
              METHOD_SIGNATURE +
              '] SKIPPED - target category "' +
              targetCategoryId +
              '" has manual selection (variant: ' +
              manualInfo.variantId +
              ')'
          );
        return;
      }

      // Resolve productId if not provided
      if (!targetProductId) {
        targetProductId = resolveProductId(targetVariantId, conductor);
      }

      if (!targetProductId) {
        isDev() &&
          console.warn(
            '[' +
              METHOD_SIGNATURE +
              '] Could not resolve productId for target variant ' +
              targetVariantId +
              ' - skipping'
          );

        publishEvent(
          CONSTANTS.EVENTS.FORBIDDEN_AUTO_APPLY_FAILED,
          {
            ruleId: ruleId,
            targetVariantId: targetVariantId,
            reason: 'product_not_found',
            source: METHOD_SIGNATURE,
          },
          METHOD_SIGNATURE
        );

        return;
      }

      // Resolve categoryId if not provided
      if (!targetCategoryId && conductor.getProductByVariantId) {
        var product = conductor.getProductByVariantId(targetVariantId);
        // CRITICAL FIX: PPD product objects use "category" field, not "categoryId" or "category_id"
        targetCategoryId = product
          ? product.category ||
            product.categoryId ||
            product.category_id ||
            null
          : null;
      }

      // Track this auto-apply rule as active
      activeAutoApplyRules.set(ruleId, {
        conditionVariantId: conditionVariantId,
        conditionCategoryId: detail.conditionCategoryId || null,
        targetVariantId: targetVariantId,
        targetCategoryId: targetCategoryId,
        targetProductId: targetProductId,
        enforcedPair: enforcedPair,
        timestamp: Date.now(),
      });

      // Track in coupling chain for circular detection
      var parentChain = autoApplyChains.get(String(conditionVariantId));
      autoApplyChains.set(String(targetVariantId), {
        sourceVariantId: conditionVariantId,
        ruleId: ruleId,
        depth: (parentChain ? parentChain.depth : 0) + 1,
      });

      // STAGE 3: PROGRESS - Execute via conductor delegation (CCP compliant)
      // SAFEGUARD 1: Tag as auto_applied with source for infinite loop prevention
      isDev() &&
        console.log(
          '[' + METHOD_SIGNATURE + '] EXECUTING auto-apply via conductor',
          {
            categoryId: targetCategoryId,
            productId: targetProductId,
            variantId: targetVariantId,
            ruleId: ruleId,
          }
        );

      var applyResult = conductor.handleStateChange('APPLY_SELECTION', {
        categoryId: targetCategoryId,
        productId: targetProductId,
        variantId: targetVariantId,
        auto_applied: true,
        isOneStep: true,
        source: 'forbidden_auto_apply',
        sourceRuleId: ruleId,
        sourceVariantId: conditionVariantId,
      });

      // ENFORCED_PAIR: the conductor can refuse the apply (a DISABLE rule
      // blocks the target, validation fails). Confirm the target actually
      // landed before taking the category away from the customer's manual
      // tracking or telling them it changed.
      if (enforcedPair) {
        if (
          !isVariantAppliedIn(
            conductor,
            targetCategoryId,
            targetVariantId,
            applyResult
          )
        ) {
          activeAutoApplyRules.delete(ruleId);
          autoApplyChains.delete(String(targetVariantId));
          publishEvent(
            CONSTANTS.EVENTS.FORBIDDEN_AUTO_APPLY_FAILED,
            {
              ruleId: ruleId,
              conditionVariantId: conditionVariantId,
              targetVariantId: targetVariantId,
              targetCategoryId: targetCategoryId,
              reason: 'enforced_apply_rejected',
              source: METHOD_SIGNATURE,
            },
            METHOD_SIGNATURE
          );
          return;
        }

        if (targetCategoryId) {
          if (manualSelections.has(targetCategoryId)) {
            isDev() &&
              console.log(
                '[' +
                  METHOD_SIGNATURE +
                  '] ENFORCED_PAIR overrode manual selection in "' +
                  targetCategoryId +
                  '"'
              );
            manualSelections.delete(targetCategoryId);
          }
          // Any other rule that auto-applied into this category just had its
          // target replaced; its tracking is stale.
          activeAutoApplyRules.forEach(function (otherState, otherRuleId) {
            if (
              otherRuleId !== ruleId &&
              otherState.targetCategoryId === targetCategoryId
            ) {
              activeAutoApplyRules.delete(otherRuleId);
              if (otherState.targetVariantId) {
                autoApplyChains.delete(String(otherState.targetVariantId));
              }
            }
          });
        }
      }

      // Track this auto-apply in the existing autoAppliedCategories map too
      if (targetCategoryId) {
        autoAppliedCategories.set(targetCategoryId, {
          variantId: targetVariantId,
          timestamp: Date.now(),
          source: 'forbidden_auto_apply',
          ruleId: ruleId,
        });
      }

      // ENFORCED_PAIR: tell the customer why a category they may not be looking
      // at just changed (classic auto-apply stays silent, as before).
      if (enforcedPair && targetCategoryId) {
        publishEnforcedPairNotice(
          targetCategoryId,
          buildForceAppliedMessage(
            detail.ruleDescription,
            targetCategoryId,
            detail.conditionCategoryId
          ),
          'force_apply',
          ruleId,
          targetVariantId
        );
      }

      var stepDuration =
        (window.performance ? window.performance.now() : Date.now()) -
        startTime;

      // STAGE 4: SUCCESS - Publish completion event
      publishEvent(
        CONSTANTS.EVENTS.FORBIDDEN_AUTO_APPLY_COMPLETED,
        {
          ruleId: ruleId,
          conditionVariantId: conditionVariantId,
          targetVariantId: targetVariantId,
          targetCategoryId: targetCategoryId,
          targetProductId: targetProductId,
          duration: parseFloat(stepDuration.toFixed(2)),
          source: METHOD_SIGNATURE,
        },
        METHOD_SIGNATURE
      );

      trackPerformance('SINGLE_STEP', stepDuration, {
        operation: 'forbidden_auto_apply',
        ruleId: ruleId,
        targetCategoryId: targetCategoryId,
      });
    } catch (error) {
      // STAGE 5: FAILED
      isDev() && console.error('[' + METHOD_SIGNATURE + '] Failed:', error);

      publishEvent(
        CONSTANTS.EVENTS.FORBIDDEN_AUTO_APPLY_FAILED,
        {
          error: error.message,
          stack: error.stack,
          ruleId: eventData.ruleId || 'unknown',
          source: METHOD_SIGNATURE,
        },
        METHOD_SIGNATURE
      );
    }
  }

  /**
   * True when `variantId` is the applied selection in `categoryId`. Falls back
   * to the conductor's return value when the applied map is unavailable.
   */
  function isVariantAppliedIn(conductor, categoryId, variantId, applyResult) {
    if (
      conductor &&
      typeof conductor.getAppliedSelectionsMap === 'function' &&
      categoryId
    ) {
      var appliedMap = conductor.getAppliedSelectionsMap();
      if (appliedMap instanceof Map) {
        var selection = appliedMap.get(categoryId);
        return !!(
          selection &&
          selection.variantId !== undefined &&
          String(selection.variantId) === String(variantId)
        );
      }
    }
    return !!applyResult;
  }

  /**
   * Drop autoApplyChains links whose key (the variant a rule applied) or whose
   * source (the variant that caused it) is no longer applied in any category.
   * Used on the ENFORCED_PAIR forward path only.
   */
  function pruneStaleChains(conductor) {
    if (
      autoApplyChains.size === 0 ||
      !conductor ||
      typeof conductor.getAppliedSelectionsMap !== 'function'
    ) {
      return;
    }
    var appliedMap = conductor.getAppliedSelectionsMap();
    if (!(appliedMap instanceof Map)) return;
    var appliedIds = new Set();
    appliedMap.forEach(function (selection) {
      if (selection && selection.variantId !== undefined) {
        appliedIds.add(String(selection.variantId));
      }
    });
    autoApplyChains.forEach(function (chain, key) {
      // A link "key was applied because of source" is only live while BOTH
      // are still applied; otherwise it records history, not a dependency.
      if (
        !appliedIds.has(String(key)) ||
        !chain ||
        !appliedIds.has(String(chain.sourceVariantId))
      ) {
        autoApplyChains.delete(key);
      }
    });
  }

  // ============================================================================
  // ENFORCED_PAIR: customer notice + reverse-direction clear
  // ============================================================================
  /**
   * Customer-facing copy for a forced apply. The rule's own description
   * (rule_description, merchant-authored) is preferred; the fallback is built
   * from category names so no product name is ever hardcoded here.
   */
  function buildForceAppliedMessage(
    ruleDescription,
    targetCategoryId,
    conditionCategoryId
  ) {
    var description =
      typeof ruleDescription === 'string' ? ruleDescription.trim() : '';
    if (description) return description;
    return conditionCategoryId
      ? targetCategoryId +
          ' was updated to match your ' +
          conditionCategoryId +
          ' choice.'
      : targetCategoryId + ' was updated to match your other choices.';
  }

  function buildMismatchMessage(targetCategoryId, conditionCategoryId) {
    return conditionCategoryId
      ? targetCategoryId +
          ' has to match your ' +
          conditionCategoryId +
          ' choice. Please update ' +
          targetCategoryId +
          ' or ' +
          conditionCategoryId +
          ' to add to cart.'
      : targetCategoryId +
          ' has to match your other choices. Please update it to add to cart.';
  }

  function buildClearedMessage(clearedCategoryId, triggerCategoryId) {
    return triggerCategoryId
      ? clearedCategoryId +
          ' was cleared because it has to match your ' +
          triggerCategoryId +
          ' choice. Please choose a ' +
          clearedCategoryId +
          '.'
      : clearedCategoryId +
          ' was cleared to match your other choices. Please choose a ' +
          clearedCategoryId +
          '.';
  }

  /**
   * PATTERN 10: UI Update Events - the ui-updater renders it (textContent only).
   */
  function publishEnforcedPairNotice(
    categoryId,
    message,
    kind,
    ruleId,
    variantId
  ) {
    if (!categoryId || !message) return;
    publishEvent(
      CONSTANTS.EVENTS.UI_ENFORCED_PAIR_NOTICE_REQUESTED,
      {
        categoryId: categoryId,
        message: message,
        kind: kind,
        ruleId: ruleId || null,
        // The variant the notice explains (force-apply only). The ui-updater
        // keeps the notice when an automatic re-apply lands that SAME variant.
        variantId: variantId ? String(variantId) : null,
        source: 'CascadeAutoSelectionCoordinator.enforcedPair',
      },
      'enforcedPair.notice'
    );
  }

  /**
   * Handles forbidden:enforced_pair_clear_triggered.
   *
   * The customer picked a non-target option in an ENFORCED_PAIR rule's target
   * category while the rule's condition is still applied elsewhere (e.g. a real
   * Back Casing while Back Entablature is "No"). The condition's category is
   * cleared so the customer re-picks it; ATC stays blocked until they do because
   * the category is required.
   *
   * Tracking is scrubbed BEFORE the conductor publishes STATE_SELECTION_CLEARED
   * so the on-clear unapply evaluation (handleSelectionClearedForAutoApply)
   * cannot act on stale entries and double-clear the category just picked.
   *
   * PATTERN 3: 5-Stage Event Lifecycle (TRIGGERED -> COMPLETED | FAILED)
   * PATTERN 14: Try/Catch + Failed Events
   *
   * @param {Object} eventData - { ruleId, clearCategoryId, clearedVariantId, triggerCategoryId, triggerVariantId }
   * @param {Object} conductor - The central conductor for delegation
   */
  function handleEnforcedPairClearTriggered(eventData, conductor) {
    var METHOD_SIGNATURE =
      'CascadeAutoSelectionCoordinator.handleEnforcedPairClearTriggered';
    var startTime = window.performance ? window.performance.now() : Date.now();
    var detail = (eventData && (eventData.detail || eventData)) || {};

    try {
      var ruleId =
        typeof detail.ruleId === 'string'
          ? detail.ruleId
          : String(detail.ruleId || '');
      var clearCategoryId =
        typeof detail.clearCategoryId === 'string'
          ? detail.clearCategoryId
          : null;
      var clearedVariantId = detail.clearedVariantId
        ? String(detail.clearedVariantId)
        : null;
      var triggerCategoryId =
        typeof detail.triggerCategoryId === 'string'
          ? detail.triggerCategoryId
          : null;

      if (!clearCategoryId) {
        throw new Error('missing clearCategoryId');
      }
      if (!conductor || typeof conductor.clearCategoryForEnforcedPair !== 'function') {
        throw new Error('conductor.clearCategoryForEnforcedPair not available');
      }

      // 1. Scrub ONLY the tracking this clear invalidates:
      //    - entries that target the cleared category (their target is gone), and
      //    - entries that target the trigger category (the customer just replaced
      //      that target by hand; left in place, the on-clear unapply below would
      //      clear the customer's own pick).
      //    An unrelated third rule that was merely CONDITIONED on the cleared
      //    category stays tracked, so handleSelectionClearedForAutoApply can
      //    unapply its target the normal way once the clear is published.
      activeAutoApplyRules.forEach(function (ruleState, trackedRuleId) {
        var targetsCleared = ruleState.targetCategoryId === clearCategoryId;
        var targetsTrigger =
          !!triggerCategoryId &&
          ruleState.targetCategoryId === triggerCategoryId;
        if (targetsCleared || targetsTrigger) {
          activeAutoApplyRules.delete(trackedRuleId);
          if (ruleState.targetVariantId) {
            autoApplyChains.delete(String(ruleState.targetVariantId));
          }
        }
      });
      if (clearedVariantId) {
        autoApplyChains.delete(clearedVariantId);
      }
      autoAppliedCategories.delete(clearCategoryId);
      // HOLD the emptied category: it must stay empty until the CUSTOMER
      // chooses. Without this, the material-preference coordinator's
      // single-compatible auto-apply (SELECTION_AUTO_APPLY_REQUEST, guarded
      // here only by manualSelections) silently refills it on the next
      // recompute, re-enabling Add to Cart with a choice nobody made. The hold
      // is an ordinary manualSelections entry with no variant, so every
      // existing "user intent is sacred" guard honours it. Released when the
      // customer applies there (handleVariantApplied overwrites it), when
      // anything else actually fills it, on user unapply, on reset, and by an
      // enforced FORCE into the category once that apply lands.
      manualSelections.set(clearCategoryId, {
        variantId: null,
        enforcedPairHold: true,
        timestamp: Date.now(),
      });

      // 2. Conductor does the state change and every publish (Pattern 20).
      var cleared = conductor.clearCategoryForEnforcedPair(clearCategoryId, {
        sourceRuleId: ruleId,
        triggerCategoryId: triggerCategoryId,
      });

      if (!cleared) {
        throw new Error('clear did not remove a selection');
      }

      // 3. Tell the customer why their other pick disappeared.
      publishEnforcedPairNotice(
        clearCategoryId,
        buildClearedMessage(clearCategoryId, triggerCategoryId),
        'clear',
        ruleId
      );

      var duration =
        (window.performance ? window.performance.now() : Date.now()) -
        startTime;
      publishEvent(
        CONSTANTS.EVENTS.FORBIDDEN_ENFORCED_PAIR_CLEAR_COMPLETED,
        {
          ruleId: ruleId,
          clearCategoryId: clearCategoryId,
          triggerCategoryId: triggerCategoryId,
          duration: parseFloat(duration.toFixed(2)),
          source: METHOD_SIGNATURE,
        },
        METHOD_SIGNATURE
      );
      trackPerformance('SINGLE_STEP', duration, {
        operation: 'enforced_pair_clear',
        ruleId: ruleId,
        clearCategoryId: clearCategoryId,
      });
    } catch (error) {
      isDev() && console.error('[' + METHOD_SIGNATURE + '] Failed:', error);
      publishEvent(
        CONSTANTS.EVENTS.FORBIDDEN_ENFORCED_PAIR_CLEAR_FAILED,
        {
          error: error.message,
          ruleId: detail.ruleId || 'unknown',
          clearCategoryId: detail.clearCategoryId || null,
          source: METHOD_SIGNATURE,
        },
        METHOD_SIGNATURE
      );
    }
  }

  /**
   * Handles forbidden:enforced_pair_atc_blocked (conductor add-to-cart guard).
   * Shows the notice under each offending category and highlights the first
   * one with the existing pending-category callout, so the customer can see
   * why Add to Cart did nothing.
   *
   * @param {Object} eventData - { violations: Array }
   */
  function handleEnforcedPairAtcBlocked(eventData) {
    try {
      var detail = (eventData && (eventData.detail || eventData)) || {};
      var violations = Array.isArray(detail.violations)
        ? detail.violations
        : [];
      var noticed = new Set();
      violations.forEach(function (violation) {
        var categoryId =
          violation && typeof violation.targetCategoryId === 'string'
            ? violation.targetCategoryId
            : null;
        if (!categoryId || noticed.has(categoryId)) return;
        noticed.add(categoryId);
        publishEvent(
          CONSTANTS.EVENTS.UI_ENFORCED_PAIR_NOTICE_REQUESTED,
          {
            categoryId: categoryId,
            message: buildMismatchMessage(
              categoryId,
              violation.conditionCategoryId
            ),
            kind: 'atc_blocked',
            reveal: noticed.size === 1,
            ruleId: violation.ruleId || null,
            source: 'CascadeAutoSelectionCoordinator.enforcedPair',
          },
          'enforcedPair.atcBlocked'
        );
        if (noticed.size === 1) {
          publishEvent(
            CONSTANTS.EVENTS.UI_PENDING_CATEGORY_CALLOUT,
            {
              categoryId: categoryId,
              reason: 'enforced_pair_mismatch',
              timestamp: Date.now(),
            },
            'enforcedPair.atcBlocked'
          );
        }
      });
    } catch (error) {
      isDev() &&
        console.error(
          '[CascadeAutoSelectionCoordinator] handleEnforcedPairAtcBlocked failed:',
          error
        );
    }
  }

  // ============================================================================
  // AUTO_APPLY_OPTION: Handle auto-unapply triggered event
  // ============================================================================
  /**
   * Handles forbidden:auto_unapply_triggered events.
   * Validates the request, checks that the rule is active, verifies no manual
   * override, and delegates to the conductor to remove the selection.
   *
   * PATTERN 3: 5-Stage Event Lifecycle
   * PATTERN 14: Try/Catch + Failed Events
   *
   * @param {Object} eventData - Event payload from forbidden:auto_unapply_triggered
   * @param {Object} conductor - The central conductor for delegation
   */
  function handleForbiddenAutoUnapplyTriggered(eventData, conductor) {
    var METHOD_SIGNATURE =
      'CascadeAutoSelectionCoordinator.handleForbiddenAutoUnapplyTriggered';
    var startTime = window.performance ? window.performance.now() : Date.now();

    try {
      var detail = eventData.detail || eventData;

      var ruleId =
        typeof detail.ruleId === 'string'
          ? detail.ruleId
          : String(detail.ruleId || '');
      var targetCategoryId = detail.targetCategoryId || null;
      var targetVariantId = detail.targetVariantId
        ? String(detail.targetVariantId)
        : null;

      if (!ruleId) {
        isDev() &&
          console.warn(
            '[' +
              METHOD_SIGNATURE +
              '] Invalid auto_unapply event: missing ruleId'
          );
        return;
      }

      // GUARD: Only unapply if rule is actually active
      if (!activeAutoApplyRules.has(ruleId)) {
        isDev() &&
          console.log(
            '[' +
              METHOD_SIGNATURE +
              '] SKIPPED - rule "' +
              ruleId +
              '" is not active'
          );
        return;
      }

      var ruleState = activeAutoApplyRules.get(ruleId);
      var effectiveTargetCategoryId =
        targetCategoryId || ruleState.targetCategoryId;
      var effectiveTargetVariantId =
        targetVariantId || ruleState.targetVariantId;

      // GUARD: Don't unapply if user has manually selected something in the target category
      // User manual override breaks the bidirectional coupling
      if (
        effectiveTargetCategoryId &&
        manualSelections.has(effectiveTargetCategoryId)
      ) {
        isDev() &&
          console.log(
            '[' +
              METHOD_SIGNATURE +
              '] SKIPPED - target category "' +
              effectiveTargetCategoryId +
              '" has manual selection override. Breaking coupling for rule "' +
              ruleId +
              '"'
          );

        // Clean up the rule tracking since coupling is broken
        activeAutoApplyRules.delete(ruleId);
        autoApplyChains.delete(String(effectiveTargetVariantId));

        return;
      }

      isDev() &&
        console.log(
          '[' +
            METHOD_SIGNATURE +
            '] EXECUTING auto-unapply via direct state manager clearing',
          {
            ruleId: ruleId,
            targetCategoryId: effectiveTargetCategoryId,
            targetVariantId: effectiveTargetVariantId,
          }
        );

      // EXECUTE: Remove selection via conductor delegation
      // CCP COMPLIANT: Uses conductor.clearCategoryState() instead of direct stateManager access.
      // This provides programmatic state clearing decoupled from user-initiated back navigation
      // (no restoreThumbnailView, no price update side-effects from handleStateChange('BACK_CLICKED')).
      // The STATE_SELECTION_CLEARED event published below triggers downstream coordination.
      if (effectiveTargetCategoryId) {
        var conductor =
          window.MurphyDoorConfigurator.conductor ||
          window.MurphyDoorConfigurator.main;
        if (conductor && typeof conductor.clearCategoryState === 'function') {
          var clearResult = conductor.clearCategoryState(
            effectiveTargetCategoryId
          );

          if (clearResult && clearResult.clearSelection) {
            // Clean up tracking maps ONLY if the clear operation was successful.
            // This must happen BEFORE the event is published to prevent race conditions.
            activeAutoApplyRules.delete(ruleId);
            autoApplyChains.delete(String(effectiveTargetVariantId));
            autoAppliedCategories.delete(effectiveTargetCategoryId);

            // Reset the UI for the auto-unapplied category (DOM must match cleared state).
            // This must happen BEFORE STATE_SELECTION_CLEARED so the DOM is in sync
            // before cascade subscribers fire.
            // CCP-compliant: publish event instead of direct uiUpdater module call (Rule 2 & 4).
            var eventBusRef = window.MurphyDoorConfigurator.CoreEventBus;
            if (eventBusRef) {
              try {
                eventBusRef.publish(
                  CONSTANTS.EVENTS.UI_USER_UNAPPLY_REQUESTED,
                  {
                    categoryId: effectiveTargetCategoryId,
                    source:
                      'cascadeAutoSelectionCoordinator.handleForbiddenAutoUnapplyTriggered',
                    auto: true,
                    timestamp: Date.now(),
                  }
                );
              } catch (uiError) {
                isDev() &&
                  console.error(
                    '[' +
                      METHOD_SIGNATURE +
                      '] UI reset event failed for auto-unapplied category "' +
                      effectiveTargetCategoryId +
                      '":',
                    uiError
                  );
              }
            }

            publishEvent(
              CONSTANTS.EVENTS.STATE_SELECTION_CLEARED,
              {
                categoryId: effectiveTargetCategoryId,
                source: 'forbidden_auto_unapply',
                sourceRuleId: ruleId,
              },
              METHOD_SIGNATURE
            );
          }
        } else {
          isDev() &&
            console.error(
              '[' +
                METHOD_SIGNATURE +
                '] conductor.clearCategoryState not available'
            );
        }
      }

      var stepDuration =
        (window.performance ? window.performance.now() : Date.now()) -
        startTime;

      // Publish completion event
      publishEvent(
        CONSTANTS.EVENTS.FORBIDDEN_AUTO_UNAPPLY_COMPLETED,
        {
          ruleId: ruleId,
          targetCategoryId: effectiveTargetCategoryId,
          targetVariantId: effectiveTargetVariantId,
          duration: parseFloat(stepDuration.toFixed(2)),
          source: METHOD_SIGNATURE,
        },
        METHOD_SIGNATURE
      );
    } catch (error) {
      isDev() && console.error('[' + METHOD_SIGNATURE + '] Failed:', error);

      publishEvent(
        CONSTANTS.EVENTS.FORBIDDEN_AUTO_UNAPPLY_FAILED,
        {
          error: error.message,
          stack: error.stack,
          ruleId: eventData.ruleId || 'unknown',
          source: METHOD_SIGNATURE,
        },
        METHOD_SIGNATURE
      );
    }
  }

  // ============================================================================
  // AUTO_APPLY_OPTION: Handle selection cleared for auto-unapply cascade
  // ============================================================================
  /**
   * Handles state:selection_cleared / state:selection_removed events.
   * Evaluates if any active auto-apply rules need to be un-applied because
   * the cleared category was the condition source for an auto-apply rule.
   *
   * PATTERN 4: Event Consumer Handler
   * PATTERN 14: Try/Catch + Failed Events
   *
   * @param {Object} eventData - Event payload from state:selection_cleared
   * @param {Object} conductor - The central conductor for delegation
   */
  function handleSelectionClearedForAutoApply(eventData, conductor) {
    var METHOD_SIGNATURE =
      'CascadeAutoSelectionCoordinator.handleSelectionClearedForAutoApply';

    try {
      var detail = eventData.detail || eventData;
      var clearedCategoryId = detail.categoryId || null;

      if (!clearedCategoryId) return;

      // Skip if no active auto-apply rules
      if (activeAutoApplyRules.size === 0) return;

      // CCP COMPLIANT: Delegate to conductor for forbidden validator evaluation.
      // Uses conductor.evaluateAutoApplyRulesOnClear() delegation method to route
      // business logic through the conductor instead of direct module-to-module access.
      if (
        !conductor ||
        typeof conductor.evaluateAutoApplyRulesOnClear !== 'function'
      ) {
        isDev() &&
          console.warn(
            '[' +
              METHOD_SIGNATURE +
              '] conductor.evaluateAutoApplyRulesOnClear not available'
          );
        return;
      }

      var currentSelections = conductor.getAppliedSelectionsMap
        ? conductor.getAppliedSelectionsMap()
        : null;
      var forbiddenRules = conductor.getUnifiedForbiddenRules
        ? conductor.getUnifiedForbiddenRules()
        : null;

      if (!currentSelections || !forbiddenRules) return;

      var rulesToUnapply = conductor.evaluateAutoApplyRulesOnClear(
        clearedCategoryId,
        activeAutoApplyRules,
        currentSelections,
        forbiddenRules
      );

      if (rulesToUnapply.length === 0) return;

      isDev() &&
        console.log(
          '[' +
            METHOD_SIGNATURE +
            '] Found ' +
            rulesToUnapply.length +
            ' rules to unapply after clear',
          { clearedCategoryId: clearedCategoryId, rules: rulesToUnapply }
        );

      // Publish unapply events for each rule that is no longer satisfied
      rulesToUnapply.forEach(function (ruleToUnapply) {
        publishEvent(
          CONSTANTS.EVENTS.FORBIDDEN_AUTO_UNAPPLY_TRIGGERED,
          {
            ruleId: ruleToUnapply.ruleId,
            targetCategoryId: ruleToUnapply.targetCategoryId,
            targetVariantId: ruleToUnapply.targetVariantId,
            reason: ruleToUnapply.reason,
            clearedCategoryId: clearedCategoryId,
            source: METHOD_SIGNATURE,
          },
          METHOD_SIGNATURE
        );
      });
    } catch (error) {
      isDev() && console.error('[' + METHOD_SIGNATURE + '] Failed:', error);
    }
  }

  // ============================================================================
  // STALE SELECTION DETECTION
  // ============================================================================
  /**
   * Detects auto-applied selections that may be "orphaned" after a material
   * preference change (cleared or switched to a different SKU).
   *
   * When a material preference changes, auto-applied options in other categories
   * that were driven by the PREVIOUS preference may no longer be the best choice.
   * Instead of auto-unapplying them (Phase 3), we mark them "stale" so the user
   * sees a visual indicator and can reconsider.
   *
   * PATTERN 3: 5-Stage Event Lifecycle (start → detection → publish)
   * PATTERN 14: Try/Catch + Failed Events
   *
   * @param {Object} eventData - Material preference event payload
   * @param {string} changeType - 'recomputed' or 'cleared'
   */
  function detectStaleAutoAppliedSelections(eventData, changeType) {
    var METHOD_SIGNATURE =
      'CascadeAutoSelectionCoordinator.detectStaleAutoAppliedSelections';

    try {
      // Only process if we have auto-applied categories that could be stale
      if (autoAppliedCategories.size === 0) {
        isDev() &&
          console.log(
            '[' +
              METHOD_SIGNATURE +
              '] No auto-applied categories to check for staleness'
          );
        return;
      }

      var detail = eventData.detail || eventData;
      var unappliedCategoryId = detail.unappliedCategoryId || null;
      var newStaleCategoryIds = [];

      // Check each auto-applied category: if the material preference that drove
      // its auto-selection has changed, it is potentially stale.
      // Only considers selections auto-applied due to material filtering
      // (reason === 'simplified_ux_single_compatible'). Forbidden rule auto-applies,
      // URL restorations, and other cascade reasons are NOT material-driven.
      autoAppliedCategories.forEach(function (autoApplyInfo, categoryId) {
        // Exclude the category that was just unapplied (it's handled by the unapply flow)
        if (categoryId === unappliedCategoryId) return;

        // Don't mark categories that already have the stale indicator
        if (staleCategoryIds.has(categoryId)) return;

        // Don't mark manually selected categories (user chose them intentionally)
        if (manualSelections.has(categoryId)) return;

        // Only consider selections driven by material filtering
        if (autoApplyInfo.reason !== 'simplified_ux_single_compatible') return;

        // This auto-applied category was driven by a material constraint
        // that has now changed — mark it as stale
        newStaleCategoryIds.push(categoryId);
      });

      if (newStaleCategoryIds.length === 0) {
        isDev() &&
          console.log(
            '[' +
              METHOD_SIGNATURE +
              '] No newly stale categories detected (changeType: ' +
              changeType +
              ')'
          );
        return;
      }

      // Track stale category IDs locally
      newStaleCategoryIds.forEach(function (id) {
        staleCategoryIds.add(id);
      });

      // Publish UI_STALE_SELECTION_DETECTED for the UI layer to render the indicator
      publishEvent(
        CONSTANTS.EVENTS.UI_STALE_SELECTION_DETECTED,
        {
          categoryIds: newStaleCategoryIds,
          changeType: changeType,
          materialType: detail.materialType || null,
          previousWoodSku: detail.previousWoodSku || null,
          previousFinishSku: detail.previousFinishSku || null,
          survivingWoodSku: detail.survivingWoodSku || null,
          survivingFinishSku: detail.survivingFinishSku || null,
          timestamp: Date.now(),
        },
        METHOD_SIGNATURE
      );

      isDev() &&
        console.log(
          '[' +
            METHOD_SIGNATURE +
            '] Detected ' +
            newStaleCategoryIds.length +
            ' stale auto-applied categories (changeType: ' +
            changeType +
            ')',
          {
            categoryIds: newStaleCategoryIds,
            totalStale: staleCategoryIds.size,
          }
        );
    } catch (error) {
      isDev() && console.error('[' + METHOD_SIGNATURE + '] Failed:', error);
    }
  }

  // ============================================================================
  // MODULE INITIALIZATION
  // ============================================================================
  /**
   * Initialize the cascade auto-selection coordinator.
   *
   * PATTERN 4: Event Consumer Handlers - subscribes to all required events
   * PATTERN 16: Initialization Guard - prevents double-init
   * PATTERN 15: Cleanup Arrays - tracks all subscriptions for teardown
   *
   * @param {Object} conductor - The central conductor for delegation
   * @returns {boolean} True if initialization succeeded
   */
  function initialize(conductor) {
    // PATTERN 16: Initialization Guard
    if (isInitialized) {
      isDev() &&
        console.warn(
          '[CascadeAutoSelectionCoordinator] Already initialized, skipping'
        );
      return false;
    }

    if (!conductor) {
      isDev() &&
        console.error(
          '[CascadeAutoSelectionCoordinator] Cannot initialize: conductor is required'
        );
      return false;
    }

    var bus = eventBus || window.MurphyDoorConfigurator.CoreEventBus;
    if (!bus) {
      isDev() &&
        console.error(
          '[CascadeAutoSelectionCoordinator] Cannot initialize: EventBus not available'
        );
      return false;
    }

    // Validate conductor has required methods
    if (typeof conductor.handleStateChange !== 'function') {
      isDev() &&
        console.error(
          '[CascadeAutoSelectionCoordinator] Cannot initialize: conductor missing handleStateChange method'
        );
      return false;
    }

    if (typeof conductor.getProductByVariantId !== 'function') {
      isDev() &&
        console.error(
          '[CascadeAutoSelectionCoordinator] Cannot initialize: conductor missing getProductByVariantId method'
        );
      return false;
    }

    eventBus = bus;

    try {
      // PATTERN 4: Event Consumer Handlers

      // THE CRITICAL SUBSCRIPTION: selection:auto_apply_request (was dead!)
      var unsubAutoApply = bus.subscribe(
        CONSTANTS.EVENTS.SELECTION_AUTO_APPLY_REQUEST,
        function (eventData) {
          handleAutoApplyRequest(eventData, conductor);
        }
      );
      cleanupHandlers.push(unsubAutoApply);

      // Track manual selections via selection:variant_applied
      var unsubVariantApplied = bus.subscribe(
        CONSTANTS.EVENTS.SELECTION_VARIANT_APPLIED,
        function (eventData) {
          handleVariantApplied(eventData);
        }
      );
      cleanupHandlers.push(unsubVariantApplied);

      // URL RESTORATION: Subscribe to cascade apply URL selections
      // When the url-param-parser validates and publishes selections from a ?config= URL,
      // this handler applies each selection through the existing cascade logic.
      var unsubUrlRestore = bus.subscribe(
        CONSTANTS.EVENTS.BUSINESS_CASCADE_APPLY_URL_SELECTIONS,
        function (eventData) {
          handleUrlRestorationRequest(
            eventData,
            conductor,
            resolveUrlSelectionsSource(eventData)
          );
        }
      );
      cleanupHandlers.push(unsubUrlRestore);

      // WILL IT FIT PREFILL: same apply mechanics, different provenance.
      // md-config-support-willitfit-prefill.js resolves the measuring wizard's
      // finished result to PPD variants and publishes them here.
      var unsubWillItFit = bus.subscribe(
        CONSTANTS.EVENTS.BUSINESS_CASCADE_APPLY_WILLITFIT_SELECTIONS,
        function (eventData) {
          handleUrlRestorationRequest(eventData, conductor, 'willitfit_prefill');
        }
      );
      cleanupHandlers.push(unsubWillItFit);

      // SIMPLIFIED MODE: Subscribe to cascade apply simplified defaults
      // When the simplified-mode-coordinator fires after 3 user selections,
      // this handler applies the 17 pre-built defaults through the same cascade logic.
      var unsubSimplifiedDefaults = bus.subscribe(
        CONSTANTS.EVENTS.BUSINESS_CASCADE_APPLY_SIMPLIFIED_DEFAULTS,
        function (eventData) {
          handleSimplifiedDefaultsRequest(eventData, conductor);
        }
      );
      cleanupHandlers.push(unsubSimplifiedDefaults);

      // GATE CARDS: Subscribe to cascade apply gate defaults.
      // When a gate card answer (e.g. "Cabinets? -> None") hides a real
      // category, md-config-bl-gate-card-coordinator.js resolves the real
      // "no doors" product and fires this event. Reuses the SAME, unmodified
      // bulk-apply handler as URL restoration / simplified defaults above --
      // composition, not new execution logic.
      var unsubGateDefaults = bus.subscribe(
        CONSTANTS.EVENTS.BUSINESS_CASCADE_APPLY_GATE_DEFAULTS,
        function (eventData) {
          handleSimplifiedDefaultsRequest(eventData, conductor);
        }
      );
      cleanupHandlers.push(unsubGateDefaults);

      // C2 CURATED DEFAULTS: the subscription is registered at MODULE LOAD
      // (see the bootstrap block near the top), NOT here — curated defaults fire
      // on SYSTEM_STATE_FROM_PPD_READY, which precedes this initialize() (run on
      // SYSTEM_MAPS_INITIALIZED). Subscribing here would miss the publish.

      // AUTO_APPLY_OPTION: Subscribe to forbidden:auto_apply_triggered
      // When the conductor detects an AUTO_APPLY_OPTION rule match, this handler
      // coordinates the actual application with circular dependency prevention.
      var unsubAutoApplyTriggered = bus.subscribe(
        CONSTANTS.EVENTS.FORBIDDEN_AUTO_APPLY_TRIGGERED,
        function (eventData) {
          if (!bootstrapComplete) {
            pendingCalls.push({
              fn: handleForbiddenAutoApplyTriggered,
              args: [eventData, conductor],
            });
            return;
          }
          handleForbiddenAutoApplyTriggered(eventData, conductor);
        }
      );
      cleanupHandlers.push(unsubAutoApplyTriggered);

      // ENFORCED_PAIR reverse direction: clear the condition's category
      var unsubEnforcedPairClear = bus.subscribe(
        CONSTANTS.EVENTS.FORBIDDEN_ENFORCED_PAIR_CLEAR_TRIGGERED,
        function (eventData) {
          if (!bootstrapComplete) {
            pendingCalls.push({
              fn: handleEnforcedPairClearTriggered,
              args: [eventData, conductor],
            });
            return;
          }
          handleEnforcedPairClearTriggered(eventData, conductor);
        }
      );
      cleanupHandlers.push(unsubEnforcedPairClear);

      // ENFORCED_PAIR add-to-cart guard feedback
      var unsubEnforcedPairAtcBlocked = bus.subscribe(
        CONSTANTS.EVENTS.FORBIDDEN_ENFORCED_PAIR_ATC_BLOCKED,
        function (eventData) {
          handleEnforcedPairAtcBlocked(eventData);
        }
      );
      cleanupHandlers.push(unsubEnforcedPairAtcBlocked);

      // AUTO_APPLY_OPTION: Subscribe to forbidden:auto_unapply_triggered
      // When conditions for an active auto-apply rule are no longer met,
      // this handler coordinates the removal of the auto-applied selection.
      var unsubAutoUnapplyTriggered = bus.subscribe(
        CONSTANTS.EVENTS.FORBIDDEN_AUTO_UNAPPLY_TRIGGERED,
        function (eventData) {
          if (!bootstrapComplete) {
            pendingCalls.push({
              fn: handleForbiddenAutoUnapplyTriggered,
              args: [eventData, conductor],
            });
            return;
          }
          handleForbiddenAutoUnapplyTriggered(eventData, conductor);
        }
      );
      cleanupHandlers.push(unsubAutoUnapplyTriggered);

      // AUTO_APPLY_OPTION: Subscribe to selection cleared/removed for unapply cascade
      // When a selection is cleared, evaluate if any active auto-apply rules
      // should have their targets unapplied.
      var unsubClearedForAutoApply = bus.subscribe(
        CONSTANTS.EVENTS.STATE_SELECTION_CLEARED,
        function (eventData) {
          if (!bootstrapComplete) return;
          handleSelectionClearedForAutoApply(eventData, conductor);
        }
      );
      cleanupHandlers.push(unsubClearedForAutoApply);

      // STALE INDICATOR: When material preferences change (switched or cleared),
      // detect auto-applied categories that may now be orphaned (their auto-apply
      // was driven by a material constraint that no longer exists or has changed).
      // Publishes UI_STALE_SELECTION_DETECTED so ui-updater can show a visual cue.
      var unsubMaterialRecomputed = bus.subscribe(
        CONSTANTS.EVENTS.MATERIAL_PREFERENCES_RECOMPUTED_FROM_APPLIED,
        function (eventData) {
          if (!bootstrapComplete) return;
          // Skip stale detection during cascade unapply — categories being cascade-unapplied
          // don't need stale indicators since they're being fully removed
          if (eventData && eventData.cascade) {
            isDev() &&
              console.log(
                '🎨 CascadeAutoSelectionCoordinator: Skipping stale detection during cascade unapply (recomputed)'
              );
            return;
          }
          detectStaleAutoAppliedSelections(eventData, 'recomputed');
        }
      );
      cleanupHandlers.push(unsubMaterialRecomputed);

      var unsubMaterialCleared = bus.subscribe(
        CONSTANTS.EVENTS.MATERIAL_PREFERENCE_CLEARED,
        function (eventData) {
          if (!bootstrapComplete) return;
          // Skip stale detection during cascade unapply — categories being cascade-unapplied
          // don't need stale indicators since they're being fully removed
          if (eventData && eventData.cascade) {
            isDev() &&
              console.log(
                '🎨 CascadeAutoSelectionCoordinator: Skipping stale detection during cascade unapply (cleared)'
              );
            return;
          }
          detectStaleAutoAppliedSelections(eventData, 'cleared');
        }
      );
      cleanupHandlers.push(unsubMaterialCleared);

      // STALE CLEARING: When a user manually selects or unapplies in a category,
      // clear any stale indicator for that category (user has taken action).
      var unsubStaleOnUserApply = bus.subscribe(
        CONSTANTS.EVENTS.SELECTION_VARIANT_APPLIED,
        function (eventData) {
          if (!bootstrapComplete) return;
          var detail = eventData.detail || eventData;
          var categoryId = detail.categoryId;
          if (categoryId && staleCategoryIds.has(categoryId)) {
            staleCategoryIds.delete(categoryId);
            publishEvent(
              CONSTANTS.EVENTS.UI_STALE_SELECTION_CLEARED,
              {
                categoryIds: [categoryId],
                reason: 'user_reselection',
                timestamp: Date.now(),
              },
              'staleClearing.variantApplied'
            );
          }
        }
      );
      cleanupHandlers.push(unsubStaleOnUserApply);

      // OPTIMISTIC CLEANUP: When a user unapply is REQUESTED, immediately remove
      // the autoAppliedCategories entry so concurrent forbidden rule checks don't
      // read stale state between REQUESTED and COMPLETED.
      // Pattern reference: material-preference-coordinator.js "atomic pre-delete" (~line 1049)
      var unsubStaleOnUserUnapplyRequested = bus.subscribe(
        CONSTANTS.EVENTS.SELECTION_USER_UNAPPLY_REQUESTED,
        function (eventData) {
          if (!bootstrapComplete) return;
          var detail = eventData.detail || eventData;
          var categoryId = detail.categoryId;
          if (categoryId && autoAppliedCategories.has(categoryId)) {
            isDev() &&
              console.log(
                '[CascadeAutoSelectionCoordinator] Optimistic pre-delete of autoAppliedCategories for "' +
                  categoryId +
                  '" on UNAPPLY_REQUESTED (atomic pre-delete pattern)'
              );
            autoAppliedCategories.delete(categoryId);
          }
        }
      );
      cleanupHandlers.push(unsubStaleOnUserUnapplyRequested);

      var unsubStaleOnUserUnapply = bus.subscribe(
        CONSTANTS.EVENTS.SELECTION_USER_UNAPPLY_COMPLETED,
        function (eventData) {
          if (!bootstrapComplete) return;
          var detail = eventData.detail || eventData;
          var categoryId = detail.categoryId;
          if (categoryId) {
            // User unapplied their selection — clear the manual selection guard so a
            // subsequent auto_apply_request for this category is NOT blocked.
            manualSelections.delete(categoryId);
            autoAppliedCategories.delete(categoryId);
          }
          if (categoryId && staleCategoryIds.has(categoryId)) {
            staleCategoryIds.delete(categoryId);
            publishEvent(
              CONSTANTS.EVENTS.UI_STALE_SELECTION_CLEARED,
              {
                categoryIds: [categoryId],
                reason: 'user_unapply',
                timestamp: Date.now(),
              },
              'staleClearing.userUnapply'
            );
          }
        }
      );
      cleanupHandlers.push(unsubStaleOnUserUnapply);

      // FAILURE CLEANUP: When a user unapply FAILS, clean up any stale entries
      // in autoAppliedCategories and staleCategoryIds for the failed categoryId.
      // Without this, a failed unapply leaves the cascade coordinator in an
      // inconsistent state (optimistic pre-delete already removed autoAppliedCategories,
      // but stale indicators and activeAutoApplyRules may linger).
      var unsubStaleOnUserUnapplyFailed = bus.subscribe(
        CONSTANTS.EVENTS.SELECTION_USER_UNAPPLY_FAILED,
        function (eventData) {
          if (!bootstrapComplete) return;
          var detail = eventData.detail || eventData;
          var categoryId = detail.categoryId;
          if (!categoryId) return;

          isDev() &&
            console.warn(
              '[CascadeAutoSelectionCoordinator] User unapply FAILED for "' +
                categoryId +
                '", cleaning up stale cascade state. Error: ' +
                (detail.error || 'unknown')
            );

          // Ensure autoAppliedCategories is cleared (may already be gone from optimistic pre-delete)
          autoAppliedCategories.delete(categoryId);

          // Clear any activeAutoApplyRules that targeted this category
          activeAutoApplyRules.forEach(function (ruleState, ruleId) {
            if (ruleState.targetCategoryId === categoryId) {
              isDev() &&
                console.log(
                  '[CascadeAutoSelectionCoordinator] Removing stale activeAutoApplyRule "' +
                    ruleId +
                    '" for failed unapply category "' +
                    categoryId +
                    '"'
                );
              activeAutoApplyRules.delete(ruleId);
              if (ruleState.targetVariantId) {
                autoApplyChains.delete(String(ruleState.targetVariantId));
              }
            }
          });

          // Clear stale indicator if present to avoid stuck UI state
          if (staleCategoryIds.has(categoryId)) {
            staleCategoryIds.delete(categoryId);
            publishEvent(
              CONSTANTS.EVENTS.UI_STALE_SELECTION_CLEARED,
              {
                categoryIds: [categoryId],
                reason: 'unapply_failed_cleanup',
                timestamp: Date.now(),
              },
              'staleClearing.userUnapplyFailed'
            );
          }
        }
      );
      cleanupHandlers.push(unsubStaleOnUserUnapplyFailed);

      // Mark as initialized
      isInitialized = true;

      // Publish module ready event
      publishEvent(
        CONSTANTS.EVENTS.SYSTEM_MODULE_READY,
        {
          module: 'CascadeAutoSelectionCoordinator',
          subscriptionCount: cleanupHandlers.length,
          cascadeConfig: CASCADE_CONFIG,
          timestamp: Date.now(),
        },
        'initialize'
      );

      isDev() &&
        console.log(
          '[CascadeAutoSelectionCoordinator] Initialized with ' +
            cleanupHandlers.length +
            ' event subscriptions. ' +
            'NOW SUBSCRIBING to selection:auto_apply_request (was previously dead!)'
        );

      return true;
    } catch (error) {
      isDev() &&
        console.error(
          '[CascadeAutoSelectionCoordinator] Initialization failed:',
          error
        );

      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_FAILED,
        {
          error: error.message,
          stack: error.stack,
          source: 'CascadeAutoSelectionCoordinator.initialize',
        },
        'initialize'
      );

      return false;
    }
  }

  // ============================================================================
  // CLEANUP
  // ============================================================================
  /**
   * Cleanup all event subscriptions and reset module state.
   *
   * PATTERN 15: Cleanup Arrays - unsubscribes all tracked handlers
   */
  function cleanup() {
    isDev() &&
      console.log(
        '[CascadeAutoSelectionCoordinator] Cleaning up ' +
          cleanupHandlers.length +
          ' subscriptions'
      );

    // Clear termination timer
    if (terminationTimer) {
      clearTimeout(terminationTimer);
      terminationTimer = null;
    }

    // Unsubscribe all event handlers
    cleanupHandlers.forEach(function (unsubscribe) {
      try {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      } catch (error) {
        isDev() &&
          console.error(
            '[CascadeAutoSelectionCoordinator] Cleanup handler error:',
            error
          );
      }
    });
    cleanupHandlers = [];

    // Reset all state
    isInitialized = false;
    manualSelections.clear();
    autoAppliedCategories.clear();
    staleCategoryIds.clear();
    cascadeQueue = [];
    cascadeInProgress = false;
    cascadeDepth = 0;
    cascadeStartTime = 0;

    // Reset AUTO_APPLY_OPTION state
    activeAutoApplyRules.clear();
    autoApplyChains.clear();
  }

  // ============================================================================
  // PUBLIC API (Exposed on namespace)
  // ============================================================================
  window.MurphyDoorConfigurator.cascadeAutoSelectionCoordinator = {
    /**
     * Initialize the coordinator with conductor reference.
     * @param {Object} conductor - The central conductor
     * @returns {boolean} Success
     */
    initialize: initialize,

    /**
     * Cleanup all subscriptions and reset state.
     */
    cleanup: cleanup,

    /**
     * Get current module state for debugging.
     * PATTERN 18: Returns frozen state object (public API - freeze is appropriate here).
     * @returns {Object} Current module state (frozen)
     */
    getState: function () {
      return Object.freeze({
        isInitialized: isInitialized,
        bootstrapComplete: bootstrapComplete,
        cascadeInProgress: cascadeInProgress,
        cascadeDepth: cascadeDepth,
        cascadeSessionId: cascadeSessionId,
        queueLength: cascadeQueue.length,
        manualSelectionsCount: manualSelections.size,
        autoAppliedCount: autoAppliedCategories.size,
        staleCategoryCount: staleCategoryIds.size,
        manualSelectionCategories: Array.from(manualSelections.keys()),
        autoAppliedCategoryIds: Array.from(autoAppliedCategories.keys()),
        staleCategoryIds: Array.from(staleCategoryIds),
        subscriptionCount: cleanupHandlers.length,
        pendingCallCount: pendingCalls.length,
        // AUTO_APPLY_OPTION state
        activeAutoApplyRulesCount: activeAutoApplyRules.size,
        activeAutoApplyRuleIds: Array.from(activeAutoApplyRules.keys()),
        autoApplyChainsCount: autoApplyChains.size,
      });
    },

    /**
     * Get a frozen copy of active auto-apply rules for debugging/external query.
     * PATTERN 18: Frozen State Objects.
     * @returns {Array} Array of { ruleId, conditionVariantId, targetVariantId, targetCategoryId }
     */
    getActiveAutoApplyRules: function () {
      var rules = [];
      activeAutoApplyRules.forEach(function (state, ruleId) {
        rules.push(
          Object.freeze({
            ruleId: ruleId,
            conditionVariantId: state.conditionVariantId,
            targetVariantId: state.targetVariantId,
            targetCategoryId: state.targetCategoryId,
            timestamp: state.timestamp,
          })
        );
      });
      return Object.freeze(rules);
    },

    /**
     * Read-only: is this category held empty by an enforced-pair clear?
     * Other modules reach it through conductor.isEnforcedPairHeld().
     * @param {string} categoryId
     * @returns {boolean}
     */
    isEnforcedPairHeld: isEnforcedPairHeld,

    // Expose configuration for external reference/testing
    PERFORMANCE_THRESHOLDS: PERFORMANCE_THRESHOLDS,
    CASCADE_CONFIG: CASCADE_CONFIG,
  };

  // ============================================================================
  // PHASE 2 INITIALIZATION: Conductor dependency validation
  // ============================================================================
  /**
   * Wait for system:maps_initialized to ensure conductor is fully ready,
   * then initialize with conductor reference.
   *
   * PATTERN 5: Event Replay Ready - subscribes at module load time
   */
  (function initializeCascadeCoordinator() {
    var bus = CoreEventBus || window.MurphyDoorConfigurator.CoreEventBus;

    if (!bus) {
      isDev() &&
        console.error(
          '[CascadeAutoSelectionCoordinator] EventBus not available for initialization'
        );
      return;
    }

    var unsubInit = bus.subscribe(
      CONSTANTS.EVENTS.SYSTEM_MAPS_INITIALIZED,
      function () {
        var conductor =
          window.MurphyDoorConfigurator.conductor ||
          window.MurphyDoorConfigurator.main;

        if (!conductor) {
          isDev() &&
            console.error(
              '[CascadeAutoSelectionCoordinator] Conductor not available at maps_initialized'
            );
          return;
        }

        var success = initialize(conductor);

        if (success) {
          isDev() &&
            console.log(
              '[CascadeAutoSelectionCoordinator] Successfully initialized via system:maps_initialized'
            );
        }
      }
    );
    cleanupHandlers.push(unsubInit);
  })();

  isDev() && console.log('[CascadeAutoSelectionCoordinator] Module loaded');
})(window);
